// =============================================================================
// HUB COMMERCIAL - Wizard Création Devis
// =============================================================================
// Workflow guidé en 5 étapes :
// 1. Informations générales (Client/Dossier)
// 2. Produits (sélection catalogue)
// 3. Installateur (sélection Operations)
// 4. Aides (activation MPR/CEE + mentions obligatoires)
// 5. Validation (résumé + CGV + verrouillage)
//
// NOTE: La génération des documents CEE/MPR est un processus séparé
// accessible depuis la fiche devis après verrouillage.
//
// Objectif : Un commercial crée un devis complet en moins de 3 minutes
// =============================================================================

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  AlertCircle,
  Lock,
  Save,
  User,
  Package,
  Wrench,
  Euro,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  Building2,
  MapPin,
  Phone,
  Mail,
  Calculator,
  ChevronRight,
  Info
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { isInstallateurProfile } from "../../utils/tenantProfile";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { Separator } from "../../components/ui/separator";
import { OhStatusNote } from "../../components/ui/oh-charte";
import { toast } from "sonner";
import { useConfigList } from "../../hooks/useConfigList";
import { formatClientAddress } from "../../utils/clientFormat";
import { deriveDevisMode, devisModeCapabilities } from "../../utils/devisMode";
import { estModifiableDevis } from "../../utils/devisStatut";
import {
  etapesDevisDuTenant, etapeApres, etapeAvant, totalLigneHt,
  ETAPE_INSTALLATEUR, ETAPE_AIDES, ETAPE_VALIDATION,
} from "../../utils/devisEtapes";

const API = process.env.REACT_APP_BACKEND_URL;

// Helper pour générer les headers avec authentification
const getAuthHeaders = (tenantId, contentType = null) => {
  const token = localStorage.getItem('session_token');
  return {
    "X-Tenant-ID": tenantId || "climatpac",
    ...(contentType && { "Content-Type": contentType }),
    ...(token && { "Authorization": `Bearer ${token}` })
  };
};

// =============================================================================
// ÉTAPES DU WIZARD
// =============================================================================

const ETAPES = [
  { id: 1, nom: "Client", icon: User, description: "Informations générales" },
  { id: 2, nom: "Produits", icon: Package, description: "Sélection catalogue" },
  { id: 3, nom: "Installateur", icon: Wrench, description: "Artisan RGE" },
  { id: 4, nom: "Aides", icon: Euro, description: "MPR / CEE + Mentions" },
  { id: 5, nom: "Validation", icon: CheckCircle2, description: "CGV + Enregistrer" }
];

// =============================================================================
// STATUTS VISUELS
// =============================================================================

// Chaque badge de statut = paire de tokens dual-mode (fond + texte cohérents
// en clair ET foncé). Le rendu applique `${color} ${textColor}` — plus de
// texte accent-contrast (blanc) forcé sur un fond pâle (contraste cassé).
const STATUT_STYLES = {
  draft: { label: "Brouillon", color: "bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)]", textColor: "text-[var(--oh-text-secondary)]" },
  brouillon: { label: "Brouillon", color: "bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)]", textColor: "text-[var(--oh-text-secondary)]" },
  emis: { label: "Émis", color: "bg-[var(--oh-accent-solid)]", textColor: "text-[var(--oh-accent-contrast)]" },
  locked: { label: "Verrouillé", color: "bg-[var(--oh-accent-solid)]", textColor: "text-[var(--oh-accent-contrast)]" },
  signe: { label: "Signé", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", textColor: "text-[var(--oh-success-text)]" },
  archive: { label: "Archivé", color: "bg-[var(--oh-accent-solid)]", textColor: "text-[var(--oh-accent-contrast)]" },
  refuse: { label: "Refusé", color: "bg-[var(--oh-error-bg)] border border-[var(--oh-error-border)]", textColor: "text-[var(--oh-error-text)]" },
  annule: { label: "Annulé", color: "bg-[var(--oh-error-bg)] border border-[var(--oh-error-border)]", textColor: "text-[var(--oh-error-text)]" },
  paye: { label: "Payé", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", textColor: "text-[var(--oh-success-text)]" }
};

// =============================================================================
// FORMATAGE MONÉTAIRE
// =============================================================================

const formatMontant = (montant) => {
  if (montant === null || montant === undefined) return "0 €";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(montant);
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export default function DevisWizard({ dossierId, devisId: initialDevisId, tenantId: propTenantId, onBack }) {
  // Tenant : prop > clé canonique current_tenant_slug. PAS de fallback sur un
  // tenant réel (anti-pattern fail-to-tenant) — vide → le backend résout via session.
  const tenantId = propTenantId || localStorage.getItem("current_tenant_slug") || "";

  // État du wizard
  const [etapeCourante, setEtapeCourante] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentDevisId, setCurrentDevisId] = useState(initialDevisId);

  // Données du devis
  const [devis, setDevis] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [client, setClient] = useState(null);

  // Bascule B2C/B2B (extensible) dérivée du client rattaché — JAMAIS du tenant.
  // caps pilote le masquage conditionnel (entreprise / fiscal B2C / MPR) + TVA défaut.
  const devisMode = useMemo(() => deriveDevisMode(client), [client]);
  const caps = useMemo(() => devisModeCapabilities(devisMode), [devisMode]);

  // Étapes affichées = celles des CAPACITÉS DÉCLARÉES du tenant (installateur requis,
  // régime d'aides) — ⛔ ni un slug, ni le seul type de client. Voir utils/devisEtapes.js.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const etapesIds = useMemo(() => etapesDevisDuTenant(), [tenantId]);
  const etapesActives = useMemo(() => ETAPES.filter(e => etapesIds.includes(e.id)), [etapesIds]);
  const derniereEtape = etapesIds[etapesIds.length - 1];

  // Données pour les étapes
  const [produitsCatalogue, setProduitsCatalogue] = useState([]);
  const [servicesCatalogue, setServicesCatalogue] = useState([]);
  const [installateurs, setInstallateurs] = useState([]);
  const [delegataires, setDelegataires] = useState([]);
  // Délégataires du Module Aides (collection aides_delegataires — où vivent ACE ENERGIE/
  // PREMIUM ENERGY). Distinct de `delegataires` (metier_delegataires, calcul auto Climat).
  const [aidesDelegataires, setAidesDelegataires] = useState([]);
  const [mentionsAides, setMentionsAides] = useState([]);
  
  // === VALIDATION CENTRALISÉE ===
  // Source of truth unique pour la validation de chaque étape
  const [validationEtapes, setValidationEtapes] = useState({
    1: { valide: false, erreurs: [] },  // Client
    2: { valide: false, erreurs: [] },  // Produits
    3: { valide: false, erreurs: [] },  // Installateur
    4: { valide: false, erreurs: [] },  // Aides + Mentions
    5: { valide: false, erreurs: [] }   // Validation finale + CGV
  });

  // État des sélections
  const [produitSelectionne, setProduitSelectionne] = useState(null);
  const [installateurSelectionne, setInstallateurSelectionne] = useState(null);
  const [aidesConfig, setAidesConfig] = useState({
    mpr_active: true,
    cee_active: true,
    coup_pouce_active: false
  });

  // État auto-save
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // idle, saving, saved, error
  
  // Référence pour le debounce et la détection de changements
  const lastDevisState = useRef(null);
  const saveTimeoutRef = useRef(null);

  // ==========================================================================
  // AUTO-SAVE BROUILLON - Debounce 1.5s + Détection changements réels
  // ==========================================================================
  
  // Fonction pour comparer si le devis a vraiment changé
  const hasRealChanges = useCallback((currentDevis) => {
    if (!lastDevisState.current || !currentDevis) return false;
    
    const prev = lastDevisState.current;
    const curr = currentDevis;
    
    // Comparer les champs qui comptent
    return (
      JSON.stringify(prev.lignes) !== JSON.stringify(curr.lignes) ||
      prev.installateur_id !== curr.installateur_id ||
      prev.numero !== curr.numero ||
      prev.date_devis !== curr.date_devis ||
      prev.objet !== curr.objet ||
      prev.conditions_paiement !== curr.conditions_paiement ||
      prev.notes_internes !== curr.notes_internes ||
      JSON.stringify(prev.snapshot_aides) !== JSON.stringify(curr.snapshot_aides)
    );
  }, []);
  
  // Auto-save avec debounce propre
  useEffect(() => {
    // Auto-save tant que le devis est modifiable (non signé) — inclut 'emis'
    // (règle JJ 2026-06-30 : un devis reste éditable jusqu'à la signature).
    if (!devis?.id || !estModifiableDevis(devis?.statut)) return;
    
    // Nettoyer le timeout précédent
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce 1.5 secondes
    saveTimeoutRef.current = setTimeout(async () => {
      // Vérifier s'il y a un changement réel
      if (!hasRealChanges(devis)) {
        return; // Pas de flood API si rien n'a changé
      }
      
      setAutoSaveStatus("saving");
      
      try {
        // Sauvegarder le devis - utilise PUT comme le backend l'attend
        const res = await fetch(`${API}/api/metier/devis/${devis.id}`, {
          method: "PUT",
          headers: getAuthHeaders(tenantId, "application/json"),
          credentials: 'include',
          body: JSON.stringify({
            objet: devis.objet,
            conditions_paiement: devis.conditions_paiement,
            notes_internes: devis.notes_internes,
            // Numéro manuel (OMV) — persisté tel quel ; ignoré côté TEF/Climat (champ
            // non exposé dans leur wizard → reste undefined → exclude_unset le saute).
            numero: devis.numero,
            // Date du devis éditable (OMV), même procédé que le numéro manuel.
            date_devis: devis.date_devis,
          })
        });
        
        if (res.ok) {
          // Mettre à jour la référence du dernier état sauvegardé
          lastDevisState.current = JSON.parse(JSON.stringify(devis));
          setLastSaved(new Date());
          setAutoSaveStatus("saved");
          
          // Indicateur discret - disparaît après 2 secondes
          setTimeout(() => setAutoSaveStatus("idle"), 2000);
        } else {
          setAutoSaveStatus("error");
          setTimeout(() => setAutoSaveStatus("idle"), 3000);
        }
      } catch (error) {
        console.error("Erreur auto-save:", error);
        setAutoSaveStatus("error");
        setTimeout(() => setAutoSaveStatus("idle"), 3000);
      }
    }, 1500); // Debounce 1.5 secondes
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [devis, hasRealChanges, tenantId]);
  
  // Initialiser la référence du dernier état au chargement
  useEffect(() => {
    if (devis && !lastDevisState.current) {
      lastDevisState.current = JSON.parse(JSON.stringify(devis));
    }
  }, [devis]);

  // ==========================================================================
  // CHARGEMENT INITIAL
  // ==========================================================================

  useEffect(() => {
    chargerDonnees();
  }, [dossierId, currentDevisId]);

  const chargerDonnees = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders(tenantId);
      
      // Charger le dossier
      const resDossier = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
        headers,
        credentials: 'include'
      });
      if (resDossier.ok) {
        const dossierData = await resDossier.json();
        setDossier(dossierData);
        setClient(dossierData.client);
      }

      // Charger ou créer le devis
      if (currentDevisId && currentDevisId !== "new") {
        const resDevis = await fetch(`${API}/api/metier/devis/${currentDevisId}`, {
          headers,
          credentials: 'include'
        });
        if (resDevis.ok) {
          const devisData = await resDevis.json();
          setDevis(devisData);
          
          // Restaurer l'état des aides si existant
          if (devisData.snapshot_aides) {
            setAidesConfig({
              mpr_active: devisData.snapshot_aides.mpr_active ?? true,
              cee_active: devisData.snapshot_aides.cee_active ?? true,
              coup_pouce_active: devisData.snapshot_aides.coup_pouce_active ?? false
            });
          }
          
          // Charger les mentions aides si présentes
          if (devisData.mentions_aides) {
            setMentionsAides(devisData.mentions_aides);
          }
          
          // Note: Validation is handled locally via validerToutesEtapes useEffect
        }
      } else {
        // Créer un nouveau devis
        await creerNouveauDevis();
      }

      // Charger les données de référence
      await Promise.all([
        chargerProduitsCatalogue(),
        chargerServicesCatalogue(),
        chargerInstallateurs(),
        chargerDelegataires()
      ]);

    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const creerNouveauDevis = async () => {
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const res = await fetch(`${API}/api/metier/devis`, {
        method: "POST",
        headers,
        credentials: 'include',
        // `objet` n'est PAS construit ici : le backend le compose depuis la référence
        // du dossier (`service.py`, repli `data.objet or f"Devis travaux - {reference}"`).
        // Le front lisait `dossier.référence` ACCENTUÉ — champ inexistant, c'est
        // `reference` — et envoyait donc toujours « Devis travaux - » : une chaîne NON
        // VIDE, qui neutralisait le repli backend. D'où l'objet à trou sur le PDF.
        // Une donnée = une source : c'est le backend qui compose. (#305)
        body: JSON.stringify({
          dossier_id: dossierId
        })
      });

      if (res.ok) {
        const data = await res.json();
        setDevis(data.devis);
        setCurrentDevisId(data.devis.id);
      } else {
        throw new Error("Erreur création devis");
      }
    } catch (error) {
      console.error("Erreur création devis:", error);
      toast.error("Impossible de créer le devis");
    }
  };

  const chargerProduitsCatalogue = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/catalogue/produits?limit=100`, {
        headers,
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setProduitsCatalogue(data);
      }
    } catch (error) {
      console.error("Erreur chargement catalogue:", error);
    }
  };

  const chargerServicesCatalogue = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/catalogue/services?limit=100`, {
        headers,
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setServicesCatalogue(data);
      }
    } catch (error) {
      console.error("Erreur chargement services:", error);
    }
  };

  const chargerInstallateurs = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      // Charger l'identité entreprise du tenant (source unique des RGE/assurances)
      const [resInstallateurs, resTenant] = await Promise.all([
        fetch(`${API}/api/operation/installateurs?status=actif`, {
          headers,
          credentials: 'include'
        }),
        fetch(`${API}/api/platform/tenants/${tenantId}/identite`, {
          headers,
          credentials: 'include'
        })
      ]);
      
      let tenantIdentite = null;
      if (resTenant.ok) {
        const tenantData = await resTenant.json();
        tenantIdentite = tenantData.identite;
      }
      
      if (resInstallateurs.ok) {
        const data = await resInstallateurs.json();
        
        // Enrichir l'installateur interne avec les données du tenant
        const enrichedInstallateurs = data.map(inst => {
          if (inst.type === "interne" && tenantIdentite) {
            return {
              ...inst,
              // Données légales du tenant
              raison_sociale: tenantIdentite.raison_sociale || inst.raison_sociale,
              siret: tenantIdentite.siret || inst.siret,
              // RGE depuis tenant.identite
              certifications_rge: tenantIdentite.rge_qualifications?.map(q => ({
                code: q.code,
                numero: q.numero,
                date_validite: q.date_validite
              })) || inst.certifications_rge || [],
              // Assurances depuis tenant.identite
              assurance_decennale: tenantIdentite.assurance_decennale?.compagnie || inst.assurance_decennale,
              assurance_decennale_info: tenantIdentite.assurance_decennale,
              assurance_rc_pro: tenantIdentite.assurance_rc_pro?.compagnie || inst.assurance_rc_pro,
              assurance_rc_pro_info: tenantIdentite.assurance_rc_pro,
              // Adresse
              adresse: tenantIdentite.adresse,
              code_postal: tenantIdentite.code_postal,
              ville: tenantIdentite.ville,
              telephone: tenantIdentite.telephone,
              email: tenantIdentite.email,
              // Flag pour indiquer source
              _source_tenant: true
            };
          }
          return inst;
        });
        
        setInstallateurs(enrichedInstallateurs);
        
        // 3️⃣ Pré-sélection installateur interne si tenant = climatpac
        // et pas d'installateur déjà assigné au devis
        if (tenantId === "climatpac" && !devis?.installateur_id) {
          const installateurInterne = enrichedInstallateurs.find(i => i.type === "interne");
          if (installateurInterne) {
            setInstallateurSelectionne(installateurInterne.id);
            // Auto-assigner si un devis existe
            if (devis?.id) {
              try {
                const assignHeaders = getAuthHeaders(tenantId);
                const res = await fetch(`${API}/api/metier/devis/${devis.id}/installateur/${installateurInterne.id}`, {
                  method: "PUT",
                  headers: assignHeaders,
                  credentials: 'include'
                });
                if (res.ok) {
                  const data = await res.json();
                  setDevis(data.devis);
                  console.log("✅ Installateur interne auto-assigné");
                }
              } catch (e) {
                console.warn("Impossible d'auto-assigner l'installateur:", e);
              }
            }
          }
        } else if (devis?.installateur_id) {
          // Si installateur déjà assigné, le sélectionner
          setInstallateurSelectionne(devis.installateur_id);
        }
      }
    } catch (error) {
      console.error("Erreur chargement installateurs:", error);
    }
  };

  const chargerDelegataires = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/delegataires`, {
        headers,
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setDelegataires(data);
      }
    } catch (error) {
      console.error("Erreur chargement délégataires:", error);
    }
    // Délégataires du Module Aides (aides_delegataires) — source du sélecteur de la
    // prime CEE manuelle (OMV). ACE ENERGIE y existe ; metier_delegataires est vide
    // pour OMV → on lit ici pour ne plus laisser le délégataire orphelin.
    try {
      const res2 = await fetch(`${API}/api/metier/aides/delegataires`, {
        headers: getAuthHeaders(tenantId), credentials: 'include'
      });
      if (res2.ok) setAidesDelegataires(await res2.json());
    } catch (error) {
      console.error("Erreur chargement délégataires (aides):", error);
    }
  };

  // NOTE: chargerDocumentsRequis supprimée - les documents sont générés APRÈS le devis

  // === VALIDATION CENTRALISÉE ===
  // Appelée après chaque action pour mettre à jour le state de validation
  // NOTE: L'étape Documents a été supprimée - les documents sont générés APRÈS le devis
  const validerToutesEtapes = useCallback((devisActuel, clientActuel) => {
    // Une étape ABSENTE de la composition du tenant est sans objet : elle vaut « ok »
    // (sinon elle bloquerait l'enregistrement d'un tenant qui ne la propose pas).
    const active = (id) => etapesIds.includes(id);
    const lignesProduits = devisActuel?.lignes?.filter(l => l.type_ligne !== "aide_deduite") || [];
    const aides = devisActuel?.snapshot_aides;
    // Mode dérivé du client : en B2B/CEE l'étape Aides est OPTIONNELLE (prime CEE
    // manuelle ou aucune aide → reste à charge plein). En B2C, le calcul des aides
    // résidentielles (snapshot_aides) reste requis. caps.showMPR = true ⇒ B2C.
    const capsValidation = devisModeCapabilities(deriveDevisMode(clientActuel));

    const nouvelleValidation = {
      1: { 
        valide: !!clientActuel, 
        erreurs: clientActuel ? [] : ["Client non chargé"] 
      },
      2: { 
        valide: lignesProduits.length > 0, 
        erreurs: lignesProduits.length > 0 ? [] : ["Ajoutez au moins un produit"] 
      },
      3: !active(ETAPE_INSTALLATEUR) ? { valide: true, erreurs: [] } : {
        valide: !!devisActuel?.installateur_id, 
        erreurs: devisActuel?.installateur_id ? [] : ["Sélectionnez un installateur"] 
      },
      4: (() => {
        // B2B/CEE : toujours valide (aides optionnelles). B2C : calcul requis.
        const ok = !active(ETAPE_AIDES) || !capsValidation.showMPR || aides?.total_aides !== undefined;
        return { valide: ok, erreurs: ok ? [] : ["Calculez les aides"] };
      })(),
      5: { valide: false, erreurs: [] }  // Calculé ci-dessous
    };
    
    // Étape 5 = toutes les étapes précédentes valides
    const toutesValides = [1, 2, 3, 4].every(e => nouvelleValidation[e].valide);
    const erreursGlobales = [1, 2, 3, 4]
      .filter(e => !nouvelleValidation[e].valide)
      .flatMap(e => nouvelleValidation[e].erreurs.map(err => `Étape ${e}: ${err}`));
    
    nouvelleValidation[5] = {
      valide: toutesValides,
      erreurs: erreursGlobales
    };
    
    setValidationEtapes(nouvelleValidation);
    return nouvelleValidation;
  }, [etapesIds]);

  // Mettre à jour la validation à chaque changement du devis
  useEffect(() => {
    if (devis && client) {
      validerToutesEtapes(devis, client);
    }
  }, [devis, client, validerToutesEtapes]);

  const rechargerDevis = async () => {
    if (!devis?.id) return;
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/devis/${devis.id}`, {
        headers,
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setDevis(data);
        if (data.mentions_aides) {
          setMentionsAides(data.mentions_aides);
        }
      }
    } catch (error) {
      console.error("Erreur rechargement:", error);
    }
  };

  // ==========================================================================
  // ACTIONS MÉTIER
  // ==========================================================================

  const ajouterProduit = async (produitId) => {
    if (!devis?.id) return;
    setSaving(true);
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const res = await fetch(`${API}/api/metier/devis/${devis.id}/lignes`, {
        method: "POST",
        headers,
        credentials: 'include',
        body: JSON.stringify({ produit_id: produitId, quantite: 1, ...(caps.tvaDefaut != null ? { tva_defaut: caps.tvaDefaut } : {}) })
      });

      if (res.ok) {
        const data = await res.json();
        setDevis(data.devis);
        toast.success("Produit ajouté au devis");
      } else {
        throw new Error("Erreur ajout produit");
      }
    } catch (error) {
      console.error("Erreur ajout produit:", error);
      toast.error("Impossible d'ajouter le produit");
    } finally {
      setSaving(false);
    }
  };

  const ajouterService = async (serviceId) => {
    if (!devis?.id) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${devis.id}/lignes`, {
        method: "POST",
        headers: getAuthHeaders(tenantId, "application/json"),
        credentials: 'include',
        body: JSON.stringify({ service_id: serviceId, quantite: 1, ...(caps.tvaDefaut != null ? { tva_defaut: caps.tvaDefaut } : {}) })
      });

      if (res.ok) {
        const data = await res.json();
        setDevis(data.devis);
        toast.success("Service ajouté au devis");
      } else {
        throw new Error("Erreur ajout service");
      }
    } catch (error) {
      console.error("Erreur ajout service:", error);
      toast.error("Impossible d'ajouter le service");
    } finally {
      setSaving(false);
    }
  };

  // 🔴 LE TEMPS 4 DU PARCOURS MARIO — « Mario chiffre au bureau ».
  //
  // ⚠️ RÈGLE MÉTIER, donnée par Mario : ses prestations ⛔ N'ONT PAS DE PRIX
  // FIXE — le prix dépend du chantier. Le terrain produit un BROUILLON ; le
  // chiffrage est une DÉCISION HUMAINE, prise ensuite. Mesuré sur ses 5 devis :
  // « mise en peinture de la porte cochère » vaut 2 270 € sur l'un et 1 550 €
  // sur l'autre. ⛔ Aucun prix ⛔ n'est donc dérivable du catalogue.
  //
  // ⚠️ CE QUI MANQUAIT : le backend porte `modifier_ligne(quantite,
  // prix_unitaire_ht)` depuis db3b9e49d, mais AUCUN écran ⛔ n'appelait le PUT —
  // mesuré sur le SHA servi : `prix_unitaire_ht` = 0 occurrence dans ce fichier.
  // Le seul geste de ligne était SUPPRIMER. ⇒ Corriger un prix imposait de
  // supprimer puis ré-ajouter, donc de RECONSTRUIRE le poste — et de perdre le
  // lien avec la proposition issue de la dictée.
  // 🔑 On MODIFIE la ligne EN PLACE. ⛔ On ⛔ ne la reconstruit PAS.
  // QUANTITÉ d'une ligne — même route PUT, le serveur RECALCULE montant_ht/tva/ttc et les
  // totaux du devis ; on ⛔ ne recalcule rien côté écran au-delà de l'aperçu de ligne.
  const modifierQuantiteLigne = async (ligneId, quantite) => {
    if (!devis?.id) return;
    const q = Number(quantite);
    if (!Number.isFinite(q) || q <= 0) {
      toast.error("La quantité doit être strictement positive");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(
        `${API}/api/metier/devis/${devis.id}/lignes/${ligneId}`
        + `?quantite=${encodeURIComponent(q)}`,
        { method: "PUT", headers: getAuthHeaders(tenantId), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setDevis(data.devis);
        toast.success("Quantité mise à jour");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || "Modification refusée");
      }
    } catch (error) {
      console.error("Erreur modification quantité ligne:", error);
      toast.error("Impossible de modifier la quantité");
    } finally {
      setSaving(false);
    }
  };

  const modifierPrixLigne = async (ligneId, prixHt) => {
    if (!devis?.id) return;
    const prix = Number(prixHt);
    // ⛔ `0` EST LÉGITIME : il signifie « à renseigner ». Seul un prix absent
    // ou négatif est refusé — et le backend le refuse aussi.
    if (!Number.isFinite(prix) || prix < 0) {
      toast.error("Prix invalide");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(
        `${API}/api/metier/devis/${devis.id}/lignes/${ligneId}`
        + `?prix_unitaire_ht=${encodeURIComponent(prix)}`,
        { method: "PUT", headers: getAuthHeaders(tenantId),
          credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setDevis(data.devis);          // totaux RECALCULÉS par le serveur
        toast.success("Prix mis à jour");
      } else {
        // ⚠️ Le refus est DIT — un devis émis n'est plus modifiable, et
        // l'utilisateur doit savoir pourquoi, ⛔ pas voir un champ inerte.
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || "Modification refusée");
      }
    } catch (error) {
      console.error("Erreur modification prix ligne:", error);
      toast.error("Impossible de modifier le prix");
    } finally {
      setSaving(false);
    }
  };

  // 🔑 N°980 — COMMENTAIRE DE LIGNE : appartient à CETTE ligne de CE devis
  //    (« de la salle de réception »). ⛔ Jamais écrit au catalogue. Le texte
  //    part dans le CORPS (texte libre) ; "" l'efface.
  const modifierCommentaireLigne = async (ligneId, commentaire) => {
    if (!devis?.id) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${devis.id}/lignes/${ligneId}`, {
        method: "PUT",
        headers: getAuthHeaders(tenantId, "application/json"),
        credentials: 'include',
        body: JSON.stringify({ commentaire_ligne: commentaire }),
      });
      if (res.ok) {
        const data = await res.json();
        setDevis(data.devis);
        toast.success("Commentaire de ligne enregistré");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || "Modification refusée");
      }
    } catch (error) {
      console.error("Erreur commentaire ligne:", error);
      toast.error("Impossible d'enregistrer le commentaire");
    } finally {
      setSaving(false);
    }
  };

  const supprimerLigne = async (ligneId) => {
    if (!devis?.id) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${devis.id}/lignes/${ligneId}`, {
        method: "DELETE",
        headers: getAuthHeaders(tenantId),
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setDevis(data.devis);
        toast.success("Ligne supprimée");
      }
    } catch (error) {
      console.error("Erreur suppression ligne:", error);
      toast.error("Impossible de supprimer la ligne");
    } finally {
      setSaving(false);
    }
  };

  const assignerInstallateur = async (installateurId) => {
    if (!devis?.id) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${devis.id}/installateur/${installateurId}`, {
        method: "PUT",
        headers: getAuthHeaders(tenantId),
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setDevis(data.devis);
        setInstallateurSelectionne(installateurId);
        toast.success("Installateur assigné");
      } else {
        throw new Error("Erreur assignation");
      }
    } catch (error) {
      console.error("Erreur assignation installateur:", error);
      toast.error("Impossible d'assigner l'installateur");
    } finally {
      setSaving(false);
    }
  };

  const activerAides = async () => {
    if (!devis?.id) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${devis.id}/activer-aides`, {
        method: "POST",
        headers: getAuthHeaders(tenantId, "application/json"),
        credentials: 'include',
        body: JSON.stringify(aidesConfig)
      });

      if (res.ok) {
        const data = await res.json();
        setDevis(data.devis);
        
        // Charger les mentions aides générées
        if (data.mentions_aides) {
          setMentionsAides(data.mentions_aides);
        }
        
        toast.success("Aides calculées");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur calcul aides");
      }
    } catch (error) {
      console.error("Erreur activation aides:", error);
      toast.error("Impossible de calculer les aides");
    } finally {
      setSaving(false);
    }
  };

  // Prime CEE saisie À LA MAIN (OMV) — ligne explicite, aucun calcul d'aides.
  const definirPrimeCeeManuelle = async (montant, delegataireNom, delegataireSiren, volumeKwhCumac) => {
    if (!devis?.id) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${devis.id}/prime-cee-manuelle`, {
        method: "POST",
        headers: getAuthHeaders(tenantId, "application/json"),
        credentials: 'include',
        body: JSON.stringify({
          montant: Number(montant) || 0,
          delegataire_nom: delegataireNom || null,
          delegataire_siren: delegataireSiren || null,
          volume_kwh_cumac: (volumeKwhCumac === undefined || volumeKwhCumac === null || volumeKwhCumac === "") ? null : Number(volumeKwhCumac),
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.devis) setDevis(data.devis);
        toast.success(data.message || "Prime CEE appliquée");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur prime CEE");
      }
    } catch (error) {
      console.error("Erreur prime CEE manuelle:", error);
      toast.error("Impossible d'appliquer la prime CEE");
    } finally {
      setSaving(false);
    }
  };

  // Enregistrer le devis (sans verrouiller - le verrouillage se fait au retour de signature)
  const enregistrerDevis = async () => {
    if (!devis?.id || !validationEtapes[5]?.valide) return;
    setSaving(true);
    
    try {
      // Le devis reste en statut "brouillon" jusqu'à l'émission
      // Il est entièrement modifiable tant qu'il n'est pas émis
      
      let currentStatut = devis.statut;

      // Règle JJ 2026-06-30 : un devis reste modifiable tant qu'il n'est pas
      // SIGNÉ. 'emis' (OMV) inclus → l'enregistrement reste possible (Solène
      // peut reprendre un devis émis). Seul un devis signé est verrouillé.
      if (estModifiableDevis(currentStatut)) {
        // Auto-save a déjà persisté les changements ; on confirme et on sort.
        toast.success("Devis enregistré ! Il reste modifiable tant qu'il n'est pas signé.");
        setTimeout(() => onBack?.(), 1000);
      } else {
        toast.info("Ce devis est signé : créez un nouveau devis pour le modifier.");
      }
    } catch (error) {
      console.error("Erreur enregistrement:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Mise à jour des données fiscales du client (RFR, nb_personnes)
  const updateClientFiscal = async (fiscalData) => {
    if (!client?.id) return false;
    try {
      const res = await fetch(`${API}/api/metier/clients/${client.id}`, {
        method: "PATCH",
        headers: getAuthHeaders(tenantId, "application/json"),
        credentials: 'include',
        body: JSON.stringify(fiscalData)
      });

      if (res.ok) {
        const updatedClient = await res.json();
        setClient(updatedClient);
        return true;
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de la mise à jour du client");
        return false;
      }
    } catch (error) {
      console.error("Erreur mise à jour client:", error);
      toast.error("Erreur lors de la mise à jour");
      return false;
    }
  };

  // ==========================================================================
  // NAVIGATION WIZARD
  // ==========================================================================

  // Utilise le state de validation centralisé
  const peutAvancer = useCallback(() => {
    return validationEtapes[etapeCourante]?.valide || false;
  }, [etapeCourante, validationEtapes]);

  const messageBlocage = useCallback(() => {
    const erreurs = validationEtapes[etapeCourante]?.erreurs || [];
    return erreurs.length > 0 ? erreurs[0] : "";
  }, [etapeCourante, validationEtapes]);

  const allerEtape = (etape) => {
    // Permettre de revenir en arrière ou d'avancer si étape courante valide
    if (!etapesIds.includes(etape)) return;
    if (etape < etapeCourante || validationEtapes[etapeCourante]?.valide) {
      setEtapeCourante(etape);
    }
  };

  const etapeSuivante = () => {
    const suivante = etapeApres(etapesIds, etapeCourante);
    if (peutAvancer() && suivante) {
      setEtapeCourante(suivante);
    }
  };

  const etapePrecedente = () => {
    const precedente = etapeAvant(etapesIds, etapeCourante);
    if (precedente) {
      setEtapeCourante(precedente);
    }
  };

  // ==========================================================================
  // RENDU CONDITIONNEL
  // ==========================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--oh-bg-elevated)]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[var(--oh-accent-text)] mx-auto mb-4" />
          <p className="text-[var(--oh-text-secondary)]">Chargement du devis...</p>
        </div>
      </div>
    );
  }

  const statutStyle = STATUT_STYLES[devis?.statut] || STATUT_STYLES.draft;
  const progressPercent = ((etapesIds.indexOf(etapeCourante) + 1) / etapesIds.length) * 100;

  return (
    <div className="min-h-screen bg-[var(--oh-bg)]">
      {/* Header fixe */}
      <div className="sticky top-0 z-50 bg-[var(--oh-bg-surface)] border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onBack?.()}
                className="text-[var(--oh-text-secondary)]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour dossier
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <div>
                <h1 className="text-lg font-medium text-[var(--oh-text)]">
                  Création devis
                </h1>
                {isInstallateurProfile() ? (
                  /* OMV : numéro de devis MANUEL (Sheet OMV) + date du devis ÉDITABLE,
                     même procédé. Découplés de la séquence/date auto TEF/Climat.
                     Sortent sur le PDF ({{devis_numero}} / date du document). */
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs text-[var(--oh-text-muted)]">N° devis</span>
                    <Input
                      value={devis?.numero || ""}
                      onChange={e => setDevis(d => ({ ...d, numero: e.target.value }))}
                      placeholder="N° libre (Sheet OMV)"
                      disabled={devis?.statut && !estModifiableDevis(devis.statut)}
                      className="h-7 w-44 bg-[var(--oh-bg-elevated)] border-white/10 text-[var(--oh-text)] text-sm"
                      data-testid="devis-numero-omv"
                    />
                    <span className="text-xs text-[var(--oh-text-muted)] ml-2">Date</span>
                    <Input
                      type="date"
                      value={devis?.date_devis || ""}
                      onChange={e => setDevis(d => ({ ...d, date_devis: e.target.value }))}
                      disabled={devis?.statut && !estModifiableDevis(devis.statut)}
                      className="h-7 w-40 bg-[var(--oh-bg-elevated)] border-white/10 text-[var(--oh-text)] text-sm"
                      data-testid="devis-date-omv"
                    />
                  </div>
                ) : (
                  <p className="text-sm text-[var(--oh-text-secondary)]">
                    {devis?.numero_provisoire || "Nouveau"}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Indicateur auto-save */}
              {autoSaveStatus === "saving" && (
                <span className="text-xs text-[var(--oh-text-muted)] flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Sauvegarde...
                </span>
              )}
              {autoSaveStatus === "saved" && (
                <span className="text-xs text-[var(--oh-success-text)] flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Brouillon sauvegardé
                </span>
              )}
              {autoSaveStatus === "error" && (
                <span className="text-xs text-[var(--oh-error-text)] flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Erreur sauvegarde
                </span>
              )}
              
              <Badge className={`${statutStyle.color} ${statutStyle.textColor}`}>
                {statutStyle.label}
              </Badge>
              {devis?.statut === "locked" && (
                <Lock className="w-5 h-5 text-[var(--oh-accent-text)]" />
              )}
            </div>
          </div>

          {/* Barre de progression */}
          <div className="mt-4">
            <Progress value={progressPercent} className="h-2" />
          </div>

          {/* Indicateurs d'étapes — barre horizontale scrollable en mobile
              (charte §9 : pas de débordement de la page). Chaque étape est un
              <Button> ghost (primitive charte) restylée en repère cliquable. */}
          <div className="flex items-center justify-between mt-4 gap-1 overflow-x-auto">
            {etapesActives.map((etape, index) => {
              const isActive = etapeCourante === etape.id;
              const isCompleted = etapeCourante > etape.id;
              const Icon = etape.icon;

              return (
                <Button
                  key={etape.id}
                  type="button"
                  variant="ghost"
                  onClick={() => allerEtape(etape.id)}
                  disabled={etape.id > etapeCourante && !peutAvancer()}
                  aria-label={`Étape ${index + 1} : ${etape.nom}`}
                  data-testid={`wizard-etape-${etape.id}`}
                  className={`shrink-0 rounded-lg transition-all ${
                    isActive
                      ? "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]"
                      : isCompleted
                      ? "text-[var(--oh-success-text)] hover:bg-[var(--oh-success-bg)]"
                      : "text-[var(--oh-text-muted)]"
                  } ${etape.id <= etapeCourante ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                  style={{ display: "flex", flexDirection: "column", height: "auto", gap: "var(--oh-space-1)", padding: "var(--oh-space-2)" }}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive
                        ? "bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]"
                        : isCompleted
                        ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]"
                        : "bg-[var(--oh-bg-surface)] text-[var(--oh-text-secondary)]"
                    }`}
                  >
                    {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className="text-xs font-medium">{etape.nom}</span>
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Message de blocage si nécessaire */}
        {!peutAvancer() && (
          <OhStatusNote variant="info" className="mb-6" data-testid="wizard-message-blocage">
            {messageBlocage()}
          </OhStatusNote>
        )}

        {/* Étape 1 : Informations générales */}
        {etapeCourante === 1 && (
          <EtapeClient client={client} dossier={dossier} devis={devis} caps={caps} />
        )}

        {/* Étape 2 : Produits */}
        {etapeCourante === 2 && (
          <EtapeProduits
            devis={devis}
            produitsCatalogue={produitsCatalogue}
            servicesCatalogue={servicesCatalogue}
            onAjouter={ajouterProduit}
            onAjouterService={ajouterService}
            onSupprimer={supprimerLigne}
            onModifierPrix={modifierPrixLigne}
            onModifierQuantite={modifierQuantiteLigne}
            onModifierCommentaire={modifierCommentaireLigne}
            saving={saving}
          />
        )}

        {/* Étape 3 : Installateur */}
        {etapeCourante === 3 && (
          <EtapeInstallateur
            devis={devis}
            installateurs={installateurs}
            onSelectionner={assignerInstallateur}
            saving={saving}
          />
        )}

        {/* Étape 4 : Aides + Mentions */}
        {etapeCourante === 4 && (
          <EtapeAides
            devis={devis}
            client={client}
            caps={caps}
            aidesConfig={aidesConfig}
            setAidesConfig={setAidesConfig}
            onCalculer={activerAides}
            onUpdateClient={updateClientFiscal}
            onPrimeCeeManuelle={definirPrimeCeeManuelle}
            saving={saving}
            mentionsAides={mentionsAides}
            aidesDelegataires={aidesDelegataires}
            dossier={dossier}
            tenantId={tenantId}
            onApplied={(d) => setDevis(d)}
            onDossierUpdated={(d) => setDossier(d)}
            dossierId={dossierId}
          />
        )}

        {/* Étape 5 : Validation + CGV */}
        {etapeCourante === 5 && (
          <EtapeValidation
            devis={devis}
            dossier={dossier}
            validation={validationEtapes[5]}
            onEnregistrer={enregistrerDevis}
            saving={saving}
            installateurs={installateurs}
            tenantId={tenantId}
            etapesIds={etapesIds}
          />
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          <Button
            variant="outline"
            onClick={etapePrecedente}
            disabled={etapeCourante === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>

          {etapeCourante !== derniereEtape ? (
            <Button
              onClick={etapeSuivante}
              disabled={!peutAvancer()}
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]"
            >
              Suivant
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={enregistrerDevis}
              disabled={!validationEtapes[5]?.valide || saving}
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer le devis
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// ÉTAPE 1 : CLIENT
// =============================================================================

function EtapeClient({ client, dossier, devis, caps = { showFiscalB2C: true, showEntreprise: false } }) {
  if (!client) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--oh-accent-text)] mx-auto mb-4" />
          <p className="text-[var(--oh-text-secondary)]">Chargement des informations client...</p>
        </CardContent>
      </Card>
    );
  }

  // Calcul de la couleur MPR si non fournie
  const calculerCouleurMpr = (rfr, nbPersonnes, region) => {
    if (!rfr || !nbPersonnes) return "—";
    
    const baremes = {
      idf: {
        1: { bleu: 23541, jaune: 28657, violet: 40018 },
        2: { bleu: 34551, jaune: 42058, violet: 58827 },
        3: { bleu: 41493, jaune: 50513, violet: 70382 },
        4: { bleu: 48447, jaune: 58981, violet: 82839 },
        5: { bleu: 55427, jaune: 67473, violet: 94844 }
      },
      province: {
        1: { bleu: 17009, jaune: 21805, violet: 30549 },
        2: { bleu: 24875, jaune: 31889, violet: 44907 },
        3: { bleu: 29917, jaune: 38349, violet: 54071 },
        4: { bleu: 34948, jaune: 44802, violet: 63235 },
        5: { bleu: 40002, jaune: 51281, violet: 72400 }
      }
    };
    
    const regionKey = region === "idf" ? "idf" : "province";
    const personneKey = Math.min(nbPersonnes, 5);
    const plafonds = baremes[regionKey][personneKey];
    
    if (!plafonds) return "rose";
    
    if (rfr <= plafonds.bleu) return "bleu";
    if (rfr <= plafonds.jaune) return "jaune";
    if (rfr <= plafonds.violet) return "violet";
    return "rose";
  };

  const couleurMpr = client.couleur_mpr || calculerCouleurMpr(
    client.rfr,
    client.nb_personnes_foyer,
    client.region_fiscale
  );
  
  // Couleur MaPrimeRénov' = donnée métier (tranche de revenu). Rendue en paires
  // de tokens stat dual-mode (fond pâle + texte foncé même famille), pas en
  // couleurs mono-mode. Chaque entrée porte SON texte → lisible clair + foncé.
  const couleurBadge = {
    bleu: "bg-[var(--oh-stat-blue-bg)] text-[var(--oh-stat-blue-text)] border border-[var(--oh-stat-blue-border)]",
    jaune: "bg-[var(--oh-stat-yellow-bg)] text-[var(--oh-stat-yellow-text)] border border-[var(--oh-stat-yellow-border)]",
    violet: "bg-[var(--oh-stat-purple-bg)] text-[var(--oh-stat-purple-text)] border border-[var(--oh-stat-purple-border)]",
    rose: "bg-[var(--oh-stat-red-bg)] text-[var(--oh-stat-red-text)] border border-[var(--oh-stat-red-border)]"
  };

  return (
    <div className="space-y-6">
      {/* Card avec fond blanc forcé pour lisibilité */}
      <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
        <CardHeader className="bg-[var(--oh-bg-surface)]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[var(--oh-accent-bg)] flex items-center justify-center">
              <User className="w-6 h-6 text-[var(--oh-accent-text)]" />
            </div>
            <div>
              <CardTitle className="text-[var(--oh-text)]">{client.raison_sociale || `${client.prenom} ${client.nom}`}</CardTitle>
              <CardDescription className="text-[var(--oh-text-secondary)]">Dossier {dossier?.référence}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-[var(--oh-bg-surface)]">
          {/* Coordonnées */}
          <div className="space-y-4">
            <h3 className="font-medium text-[var(--oh-accent-text)]">Coordonnées</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-[var(--oh-text)]">
                <Mail className="w-4 h-4 text-[var(--oh-text-muted)]" />
                {client.email || "—"}
              </div>
              <div className="flex items-center gap-2 text-[var(--oh-text)]">
                <Phone className="w-4 h-4 text-[var(--oh-text-muted)]" />
                {client.telephone || "—"}
              </div>
              <div className="flex items-center gap-2 text-[var(--oh-text)]">
                <MapPin className="w-4 h-4 text-[var(--oh-text-muted)]" />
                {formatClientAddress(client) || "—"}
              </div>
            </div>
          </div>

          {/* Bloc entreprise (B2B) — REPRISE des données du dossier rattaché
              (dossier.entreprise / dossier.representant), client en repli. Lecture
              seule : on ne re-saisit pas. Remplace les blocs fiscaux/logement masqués. */}
          {caps.showEntreprise && (() => {
            const ent = dossier?.entreprise || {};
            const rep = dossier?.representant || {};
            const siret = ent.siret || client.siret || "";
            const siren = ent.siren || client.siren || (siret ? siret.replace(/\s/g, "").slice(0, 9) : "");
            const repNom = [rep.prenom, rep.nom].filter(Boolean).join(" ")
              || [client.representant_prenom, client.representant_nom].filter(Boolean).join(" ");
            const repFonction = rep.fonction || client.representant_fonction || client.fonction;
            const repContact = [rep.email || client.email, rep.tel || rep.portable || client.telephone].filter(Boolean).join(" · ");
            return (
            <div className="space-y-4">
              <h3 className="font-medium text-[var(--oh-accent-text)]">Entreprise</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3 sm:col-span-2">
                  <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Raison sociale</p>
                  <p className="font-medium text-[var(--oh-text)]">{ent.raison_sociale || client.raison_sociale || client.societe || "—"}</p>
                </div>
                <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                  <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Secteur d'activité</p>
                  <p className="font-medium text-[var(--oh-text)]">{ent.secteur_activite || "—"}</p>
                </div>
                <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                  <p className="text-xs text-[var(--oh-text-secondary)] mb-1">SIREN</p>
                  <p className="font-medium text-[var(--oh-text)]">{siren || "—"}</p>
                </div>
                <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                  <p className="text-xs text-[var(--oh-text-secondary)] mb-1">SIRET</p>
                  <p className="font-medium text-[var(--oh-text)]">{siret || "—"}</p>
                </div>
                <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                  <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Représentant</p>
                  <p className="font-medium text-[var(--oh-text)]">
                    {[repNom, repFonction].filter(Boolean).join(" — ") || "—"}
                  </p>
                </div>
                {repContact && (
                  <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3 sm:col-span-2">
                    <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Coordonnées représentant</p>
                    <p className="font-medium text-[var(--oh-text)]">{repContact}</p>
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* Données fiscales (B2C uniquement) */}
          {caps.showFiscalB2C && (
          <div className="space-y-4">
            <h3 className="font-medium text-[var(--oh-accent-text)]">Profil fiscal</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                <p className="text-xs text-[var(--oh-text-secondary)] mb-1">RFR</p>
                <p className="font-medium text-[var(--oh-text)]">
                  {formatMontant(client.rfr)}
                </p>
              </div>
              <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Personnes</p>
                <p className="font-medium text-[var(--oh-text)]">
                  {client.nb_personnes_foyer || "—"}
                </p>
              </div>
              <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Région</p>
                <p className="font-medium text-[var(--oh-text)]">
                  {client.region_fiscale === "idf" ? "Île-de-France" : "Province"}
                </p>
              </div>
              <div className={`rounded-lg p-3 ${couleurBadge[couleurMpr] || "bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] text-[var(--oh-text-secondary)]"}`}>
                <p className="text-xs mb-1 opacity-80">Couleur MPR</p>
                <p className="font-medium capitalize">{couleurMpr}</p>
              </div>
            </div>
          </div>
          )}

          {/* Données logement (B2C uniquement) */}
          {caps.showFiscalB2C && (
          <div className="space-y-4 col-span-2">
            <h3 className="font-medium text-[var(--oh-accent-text)]">Informations logement</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Zone climatique</p>
                <p className="font-medium text-[var(--oh-text)]">
                  {devis?.snapshot_aides?.zone_climatique || dossier?.workflow_data?.tef?.infos_logement?.zone_climatique || dossier?.zone_climatique || "H1"}
                </p>
              </div>
              <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Type de logement</p>
                <p className="font-medium text-[var(--oh-text)] capitalize">
                  {devis?.snapshot_aides?.type_logement || dossier?.workflow_data?.tef?.infos_logement?.type_logement || dossier?.type_logement || "—"}
                </p>
              </div>
              <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Âge du logement</p>
                <p className="font-medium text-[var(--oh-text)]">
                  {(() => {
                    // Priorité: snapshot_aides > age_logement > annee_construction (conversion)
                    const age = devis?.snapshot_aides?.age_logement || dossier?.workflow_data?.tef?.infos_logement?.age_logement;
                    if (age === "moins_2_ans") return "- 2 ans";
                    if (age === "plus_2_ans") return "+ 2 ans";
                    if (age === "plus_15_ans") return "+ 15 ans";
                    if (age) return age;
                    
                    // Conversion depuis annee_construction si pas de age_logement
                    const annee = dossier?.workflow_data?.tef?.infos_logement?.annee_construction;
                    if (annee && !isNaN(parseInt(annee))) {
                      const ageAns = new Date().getFullYear() - parseInt(annee);
                      if (ageAns < 2) return "- 2 ans";
                      if (ageAns < 15) return "+ 2 ans";
                      return "+ 15 ans";
                    }
                    return "—";
                  })()}
                </p>
              </div>
              <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Chauffage actuel</p>
                <p className="font-medium text-[var(--oh-text)] capitalize">
                  {(() => {
                    const chauffage = devis?.snapshot_aides?.chauffage_actuel || dossier?.workflow_data?.tef?.infos_logement?.chauffage_actuel;
                    if (!chauffage) return "—";
                    // Format lisible
                    const labels = {
                      gaz: "Gaz",
                      fioul: "Fioul",
                      electrique: "Électrique",
                      bois: "Bois",
                      pac: "Pompe à chaleur",
                      autre: "Autre"
                    };
                    return labels[chauffage] || chauffage;
                  })()}
                </p>
              </div>
              <div className="bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg p-3">
                <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Surface</p>
                <p className="font-medium text-[var(--oh-text)]">
                  {(() => {
                    const surface = devis?.snapshot_aides?.surface_m2 || dossier?.workflow_data?.tef?.infos_logement?.surface_habitable;
                    return surface ? `${surface} m²` : "—";
                  })()}
                </p>
              </div>
            </div>
          </div>
          )}
        </CardContent>
      </Card>

      {/* Info box — mention couleur MPR (B2C uniquement) */}
      {caps.showFiscalB2C && (
      <div className="bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-[var(--oh-accent-text)] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-[var(--oh-accent-text)]">
            Ces informations proviennent du dossier client. La couleur MPR est calculée automatiquement 
            à partir du RFR et du nombre de personnes du foyer.
          </p>
        </div>
      </div>
      )}
    </div>
  );
}

// =============================================================================
// ÉTAPE 2 : PRODUITS ET SERVICES
// =============================================================================

function EtapeProduits({ devis, produitsCatalogue, servicesCatalogue = [], onAjouter, onAjouterService, onSupprimer, onModifierPrix, onModifierQuantite, onModifierCommentaire, saving }) {
  const [categorie, setCategorie] = useState("all");
  const [onglet, setOnglet] = useState("produits"); // "produits" ou "services"
  
  // Séparer lignes produits et lignes services
  const lignesProduits = devis?.lignes?.filter(l => l.type_ligne !== "aide_deduite" && l.type_ligne !== "service") || [];
  const lignesServices = devis?.lignes?.filter(l => l.type_ligne === "service") || [];
  const toutesLignes = devis?.lignes?.filter(l => l.type_ligne !== "aide_deduite") || [];

  // Catégories disponibles pour produits
  const { items: catProduitItems } = useConfigList("categories_produit");
  const { items: catServiceItems } = useConfigList("categories_service");
  const categoriesProduits = [{ value: "all", label: "Tous" }, ...catProduitItems.map(c => ({ value: c.id, label: c.label }))];
  const categoriesServices = [{ value: "all", label: "Tous" }, ...catServiceItems.map(c => ({ value: c.id, label: c.label }))];

  const produitsFiltres = categorie === "all"
    ? produitsCatalogue
    : produitsCatalogue.filter(p => p.categorie === categorie);

  const servicesFiltres = categorie === "all"
    ? servicesCatalogue
    : servicesCatalogue.filter(s => s.categorie === categorie);

  return (
    <div className="space-y-6">
      {/* Lignes actuelles du devis */}
      {toutesLignes.length > 0 && (
        <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
          <CardHeader className="bg-[var(--oh-bg-surface)]">
            <CardTitle className="text-lg text-[var(--oh-text)]">Produits & Services sélectionnés</CardTitle>
            <CardDescription className="text-[var(--oh-text-secondary)]">
              {lignesProduits.length} produit(s) • {lignesServices.length} service(s)
            </CardDescription>
          </CardHeader>
          <CardContent className="bg-[var(--oh-bg-surface)]">
            <div className="space-y-3">
              {toutesLignes.map((ligne) => (
                <div
                  key={ligne.id}
                  className={`p-4 rounded-lg border ${
                    ligne.type_ligne === "service" 
                      ? "bg-[var(--oh-info-bg)] border-[var(--oh-info-border)]"
                      : "bg-[var(--oh-success-bg)] border-[var(--oh-success-border)]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        ligne.type_ligne === "service"
                          ? "bg-[var(--oh-info-bg)]"
                          : "bg-[var(--oh-success-bg)]"
                      }`}>
                        {ligne.type_ligne === "service" ? (
                          <Wrench className="w-5 h-5 text-[var(--oh-info-text)]" />
                        ) : (
                          <Package className="w-5 h-5 text-[var(--oh-success-text)]" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[var(--oh-text)]">
                            {ligne.snapshot?.designation}
                          </p>
                          <Badge variant="outline" className={`text-xs ${
                            ligne.type_ligne === "service"
                              ? "text-[var(--oh-info-text)] border-[var(--oh-info-border)]"
                              : "text-[var(--oh-success-text)] border-[var(--oh-success-border)]"
                          }`}>
                            {ligne.type_ligne === "service" ? "Service" : "Produit"}
                          </Badge>
                        </div>
                        <p className="text-sm text-[var(--oh-text-secondary)]">
                          {ligne.snapshot?.marque && `${ligne.snapshot.marque} • `}
                          {ligne.snapshot?.référence}
                          {ligne.snapshot?.fiche_cee && (
                            <span className="ml-2 text-[var(--oh-success-text)]">• {ligne.snapshot.fiche_cee}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* 🔴 LE PRIX SE SAISIT ICI, SUR LA LIGNE EXISTANTE.
                          ⚠️ `defaultValue` + `onBlur` : le PUT part quand on QUITTE le champ,
                          ⛔ pas a chaque frappe. Un input controle re-rendu a chaque caractere
                          perdrait le focus — defaut deja paye sur MoneyInput.
                          La `key` porte le prix : si le serveur renvoie autre chose, le champ
                          se resynchronise au lieu de mentir. */}
                      {/* QUANTITÉ × PRIX UNITAIRE = TOTAL DE LIGNE. Même règle que pour le prix :
                          `defaultValue` + `onBlur` (le PUT part en quittant le champ) ; la `key`
                          porte la valeur serveur pour se resynchroniser. */}
                      <div className="flex items-center gap-1">
                        <input
                          type="number" min="0.01" step="any" inputMode="decimal"
                          key={`qte-${ligne.id}-${ligne.quantite}`}
                          defaultValue={ligne.quantite}
                          disabled={saving}
                          aria-label={`Quantité — ${ligne.snapshot?.designation || "ligne"}`}
                          data-testid="devis-ligne-quantite"
                          onBlur={(e) => {
                            const v = Number(e.target.value);
                            if (Number.isFinite(v) && v > 0 && v !== Number(ligne.quantite)) {
                              onModifierQuantite?.(ligne.id, v);
                            } else if (!(Number.isFinite(v) && v > 0)) {
                              e.target.value = ligne.quantite;
                            }
                          }}
                          className="w-20 h-9 px-2 rounded border border-[var(--oh-border)] bg-[var(--oh-bg-surface)] text-[var(--oh-text)] text-right text-sm"
                        />
                        <span className="text-xs text-[var(--oh-text-secondary)]" data-testid="devis-ligne-unite">
                          {ligne.unite || "u"}
                        </span>
                        <span className="text-xs text-[var(--oh-text-secondary)]">×</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="number" min="0" step="0.01" inputMode="decimal"
                          key={`pu-${ligne.id}-${ligne.prix_unitaire_ht}`}
                          defaultValue={ligne.prix_unitaire_ht}
                          disabled={saving}
                          aria-label={`Prix unitaire HT — ${ligne.snapshot?.designation || "ligne"}`}
                          data-testid="devis-ligne-prix"
                          onBlur={(e) => {
                            const v = Number(e.target.value);
                            if (Number.isFinite(v) && v !== Number(ligne.prix_unitaire_ht)) {
                              onModifierPrix?.(ligne.id, v);
                            }
                          }}
                          className="w-24 h-9 px-2 rounded border border-[var(--oh-border)] bg-[var(--oh-bg-surface)] text-[var(--oh-text)] text-right text-sm"
                        />
                        <span className="text-xs text-[var(--oh-text-secondary)]">€ HT</span>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-[var(--oh-text)]" data-testid="devis-ligne-total-ht">
                          {formatMontant(totalLigneHt(ligne.quantite, ligne.prix_unitaire_ht))} HT
                        </p>
                        <p className="text-xs text-[var(--oh-text-secondary)]" data-testid="devis-ligne-total-ttc">
                          {formatMontant(ligne.montant_ttc)} TTC
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Supprimer la ligne — ${ligne.snapshot?.designation || "ligne"}`}
                        onClick={() => onSupprimer(ligne.id)}
                        disabled={saving}
                        className="text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)]"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* 🔑 N°980 — commentaire propre à CETTE ligne de CE devis.
                      `defaultValue` + `onBlur` comme le prix ; la `key` porte la
                      valeur serveur pour se resynchroniser. */}
                  <div className="mt-3">
                    <textarea
                      key={`com-${ligne.id}-${ligne.commentaire_ligne || ""}`}
                      defaultValue={ligne.commentaire_ligne || ""}
                      disabled={saving}
                      rows={2}
                      maxLength={1000}
                      placeholder="Commentaire pour ce devis (ex. de la salle de réception)"
                      aria-label={`Commentaire de ligne — ${ligne.snapshot?.designation || "ligne"}`}
                      data-testid="devis-ligne-commentaire"
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v !== (ligne.commentaire_ligne || "")) {
                          onModifierCommentaire?.(ligne.id, v);
                        }
                      }}
                      className="w-full px-2 py-1 rounded border border-[var(--oh-border)] bg-[var(--oh-bg-surface)] text-[var(--oh-text)] text-sm"
                    />
                  </div>

                  {/* Caractéristiques techniques */}
                  {ligne.snapshot && (ligne.snapshot.puissance_kw || ligne.snapshot.cop || ligne.snapshot.etas || ligne.snapshot.scop || ligne.snapshot.classe_energie || ligne.snapshot.modele) && (
                    <div className="mt-3 pt-3 border-t border-dashed border-[var(--oh-border)]">
                      <p className="text-xs font-medium text-[var(--oh-text-secondary)] mb-2">Caractéristiques techniques</p>
                      <div className="flex flex-wrap gap-2">
                        {ligne.snapshot.modele && (
                          <span className="px-2 py-1 bg-[var(--oh-bg-surface)] rounded text-xs text-[var(--oh-text)]">
                            Modèle: {ligne.snapshot.modele}
                          </span>
                        )}
                        {ligne.snapshot.puissance_kw && (
                          <span className="px-2 py-1 bg-[var(--oh-accent-bg)] rounded text-xs text-[var(--oh-accent-text)]">
                            {ligne.snapshot.puissance_kw} kW
                          </span>
                        )}
                        {ligne.snapshot.cop && (
                          <span className="px-2 py-1 bg-[var(--oh-success-bg)] rounded text-xs text-[var(--oh-success-text)]">
                            COP: {ligne.snapshot.cop}
                          </span>
                        )}
                        {ligne.snapshot.scop && (
                          <span className="px-2 py-1 bg-[var(--oh-success-bg)] rounded text-xs text-[var(--oh-success-text)]">
                            SCOP: {ligne.snapshot.scop}
                          </span>
                        )}
                        {ligne.snapshot.etas && (
                          <span className="px-2 py-1 bg-[var(--oh-info-bg)] rounded text-xs text-[var(--oh-info-text)]">
                            ETAS: {ligne.snapshot.etas}%
                          </span>
                        )}
                        {ligne.snapshot.classe_energie && (
                          <span className="px-2 py-1 bg-[var(--oh-accent-bg)] rounded text-xs text-[var(--oh-accent-text)]">
                            Classe: {ligne.snapshot.classe_energie}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Zone de texte / Description HTML */}
                  {(ligne.snapshot?.description_html || ligne.snapshot?.description) && (
                    <div className="mt-3 pt-3 border-t border-dashed border-[var(--oh-border)]">
                      <p className="text-xs font-medium text-[var(--oh-text-secondary)] mb-1">Description</p>
                      {ligne.snapshot.description_html ? (
                        <div 
                          className="text-xs text-[var(--oh-text-secondary)] prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: ligne.snapshot.description_html }}
                        />
                      ) : (
                        <p className="text-xs text-[var(--oh-text-secondary)]">{ligne.snapshot.description}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Onglets Produits / Services */}
      <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
        <CardHeader className="pb-2 bg-[var(--oh-bg-surface)]">
          <div className="flex items-center gap-4">
            <Button
              variant={onglet === "produits" ? "default" : "outline"}
              onClick={() => { setOnglet("produits"); setCategorie("all"); }}
              className="flex items-center gap-2"
            >
              <Package className="w-4 h-4" />
              Produits ({produitsCatalogue.length})
            </Button>
            <Button
              variant={onglet === "services" ? "default" : "outline"}
              onClick={() => { setOnglet("services"); setCategorie("all"); }}
              className="flex items-center gap-2"
            >
              <Wrench className="w-4 h-4" />
              Services ({servicesCatalogue.length})
            </Button>
          </div>
        </CardHeader>

        {/* Filtres */}
        <div className="px-6 pb-4 flex flex-wrap gap-2 bg-[var(--oh-bg-surface)]">
          {(onglet === "produits" ? categoriesProduits : categoriesServices).map((cat) => (
            <Button
              key={cat.value}
              variant={categorie === cat.value ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setCategorie(cat.value)}
            >
              {cat.label}
            </Button>
          ))}
        </div>

        <CardContent className="bg-[var(--oh-bg-surface)]">
          {/* Liste Produits */}
          {onglet === "produits" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {produitsFiltres.map((produit) => {
                const dejaAjoute = toutesLignes.some(
                  l => l.snapshot?.produit_id === produit.id
                );

                return (
                  <div
                    key={produit.id}
                    className={`p-4 border rounded-lg transition-all ${
                      dejaAjoute
                        ? "bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] opacity-50"
                        : "bg-[var(--oh-bg-surface)] border-[var(--oh-border)] hover:border-[var(--oh-accent)] hover:shadow-md cursor-pointer"
                    }`}
                    onClick={() => !dejaAjoute && !saving && onAjouter(produit.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-[var(--oh-text)]">
                          {produit.designation}
                        </h4>
                        <p className="text-sm text-[var(--oh-text-secondary)]">
                          {produit.marque} • {produit.référence}
                        </p>
                        {produit.fiche_cee && (
                          <Badge variant="outline" className="mt-2 text-[var(--oh-success-text)] border-[var(--oh-success-border)]">
                            {produit.fiche_cee}
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-[var(--oh-text)]">
                          {formatMontant(produit.prix_ht)}
                        </p>
                        <p className="text-xs text-[var(--oh-text-secondary)]">
                          HT • TVA {produit.taux_tva}%
                        </p>
                      </div>
                    </div>
                    {dejaAjoute && (
                      <p className="text-xs text-[var(--oh-text-secondary)] mt-2">Déjà ajouté</p>
                    )}
                  </div>
                );
              })}

              {produitsFiltres.length === 0 && (
                <div className="col-span-2 text-center text-[var(--oh-text-secondary)] py-8">
                  Aucun produit dans cette catégorie
                </div>
              )}
            </div>
          )}

          {/* Liste Services */}
          {onglet === "services" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {servicesFiltres.map((service) => {
                const dejaAjoute = toutesLignes.some(
                  l => l.snapshot?.service_id === service.id
                );

                return (
                  <div
                    key={service.id}
                    className={`p-4 border rounded-lg transition-all ${
                      dejaAjoute
                        ? "bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] opacity-50"
                        : "bg-[var(--oh-bg-surface)] border-[var(--oh-border)] hover:border-[var(--oh-info-border)] hover:shadow-md cursor-pointer"
                    }`}
                    onClick={() => !dejaAjoute && !saving && onAjouterService(service.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-[var(--oh-text)]">
                          {service.designation}
                        </h4>
                        <p className="text-sm text-[var(--oh-text-secondary)]">
                          {service.référence}
                        </p>
                        <Badge variant="outline" className="mt-2 text-[var(--oh-info-text)] border-[var(--oh-info-border)]">
                          {service.categorie?.replace("_", " ") || "Service"}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-[var(--oh-text)]">
                          {formatMontant(service.prix_ht)}
                        </p>
                        <p className="text-xs text-[var(--oh-text-secondary)]">
                          HT • TVA {service.taux_tva}%
                        </p>
                      </div>
                    </div>
                    {dejaAjoute && (
                      <p className="text-xs text-[var(--oh-text-secondary)] mt-2">Déjà ajouté</p>
                    )}
                  </div>
                );
              })}

              {servicesFiltres.length === 0 && (
                <div className="col-span-2 text-center text-[var(--oh-text-secondary)] py-8">
                  <Wrench className="w-12 h-12 text-[var(--oh-text-muted)] mx-auto mb-4" />
                  <p>Aucun service dans cette catégorie</p>
                  <p className="text-sm text-[var(--oh-text-muted)] mt-2">
                    Créez des services dans le catalogue pour les utiliser ici
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// ÉTAPE 3 : INSTALLATEUR
// =============================================================================

function EtapeInstallateur({ devis, installateurs, onSelectionner, saving }) {
  const installateurActuel = devis?.installateur_id;

  // Trouver l'installateur sélectionné pour afficher ses détails
  const installateurSelectionne = installateurs.find(i => i.id === installateurActuel);

  return (
    <div className="space-y-6">
      {/* Installateur sélectionné - Détails */}
      {installateurSelectionne && (
        <Card className="border-[var(--oh-success-border)] bg-[var(--oh-success-bg)]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-[var(--oh-success-text)]">Installateur sélectionné</CardTitle>
              <Badge className={installateurSelectionne.type === "interne" ? "bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]" : "bg-[var(--oh-stat-orange-bg)] text-[var(--oh-stat-orange-text)] border border-[var(--oh-stat-orange-border)]"}>
                {installateurSelectionne.type === "interne" ? "Interne" : "Externe"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Identité */}
              <div className="space-y-3">
                <h4 className="font-medium text-[var(--oh-text)] border-b pb-2">Identité</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[var(--oh-text-secondary)]">Raison sociale</span>
                    <span className="font-medium">{installateurSelectionne.entreprise || installateurSelectionne.nom}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--oh-text-secondary)]">SIRET</span>
                    <span className="font-medium">{installateurSelectionne.siret || "Non renseigné"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--oh-text-secondary)]">Téléphone</span>
                    <span className="font-medium">{installateurSelectionne.telephone || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--oh-text-secondary)]">Email</span>
                    <span className="font-medium">{installateurSelectionne.email || "—"}</span>
                  </div>
                </div>
              </div>
              
              {/* Qualifications & Assurance */}
              <div className="space-y-3">
                <h4 className="font-medium text-[var(--oh-text)] border-b pb-2">Qualifications & Assurance</h4>
                <div className="space-y-2">
                  {/* Qualifications RGE */}
                  <div>
                    <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Qualifications RGE</p>
                    {installateurSelectionne.certifications_rge?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {installateurSelectionne.certifications_rge.map((cert, idx) => {
                          const code = typeof cert === 'object' ? cert.code : cert;
                          const numero = typeof cert === 'object' ? cert.numero : null;
                          const dateValidite = typeof cert === 'object' ? cert.date_validite : null;
                          return (
                            <div key={idx} className="inline-flex flex-col">
                              <Badge variant="outline" className="text-[var(--oh-success-text)] border-[var(--oh-success-border)]">
                                {code}
                                {numero && <span className="ml-1 text-[var(--oh-text-muted)]">({numero})</span>}
                              </Badge>
                              {dateValidite && (
                                <span className="text-xs text-[var(--oh-success-text)] mt-0.5">
                                  Exp. {new Date(dateValidite).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : installateurSelectionne.specialites?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {installateurSelectionne.specialites.map((spec, idx) => (
                          <Badge key={idx} variant="outline" className="text-[var(--oh-accent-text)] border-[var(--oh-accent)]">
                            {spec}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[var(--oh-info-text)]">Non renseigné</p>
                    )}
                  </div>
                  
                  {/* Assurance Décennale */}
                  <div>
                    <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Assurance décennale</p>
                    {(installateurSelectionne.assurance_decennale || installateurSelectionne.assurance_decennale_info) ? (
                      <div className="text-sm">
                        <p className="font-medium text-[var(--oh-text)]">
                          {installateurSelectionne.assurance_decennale_info?.compagnie || installateurSelectionne.assurance_decennale}
                        </p>
                        {installateurSelectionne.assurance_decennale_info?.numero_police && (
                          <p className="text-[var(--oh-text-secondary)]">N° {installateurSelectionne.assurance_decennale_info.numero_police}</p>
                        )}
                        {installateurSelectionne.assurance_decennale_info?.date_validite && (
                          <p className="text-[var(--oh-success-text)]">
                            Exp. {new Date(installateurSelectionne.assurance_decennale_info.date_validite).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-[var(--oh-info-text)]">Non renseignée</p>
                    )}
                  </div>
                  
                  {/* Assurance RC Pro */}
                  <div>
                    <p className="text-xs text-[var(--oh-text-secondary)] mb-1">RC Professionnelle</p>
                    {(installateurSelectionne.assurance_rc_pro || installateurSelectionne.assurance_rc_pro_info) ? (
                      <div className="text-sm">
                        <p className="font-medium text-[var(--oh-text)]">
                          {installateurSelectionne.assurance_rc_pro_info?.compagnie || installateurSelectionne.assurance_rc_pro}
                        </p>
                        {installateurSelectionne.assurance_rc_pro_info?.date_validite && (
                          <p className="text-[var(--oh-success-text)]">
                            Exp. {new Date(installateurSelectionne.assurance_rc_pro_info.date_validite).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-[var(--oh-text-muted)]">—</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des installateurs */}
      <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
        <CardHeader className="bg-[var(--oh-bg-surface)]">
          <CardTitle className="text-lg text-[var(--oh-text)]">
            {installateurSelectionne ? "Changer d'installateur" : "Sélectionner un installateur"}
          </CardTitle>
          <CardDescription className="text-[var(--oh-text-secondary)]">
            L'installateur doit être certifié RGE pour les équipements du devis
          </CardDescription>
        </CardHeader>
        <CardContent className="bg-[var(--oh-bg-surface)]">
          {installateurs.length === 0 ? (
            <div className="text-center py-8">
              <Wrench className="w-12 h-12 text-[var(--oh-text-muted)] mx-auto mb-4" />
              <p className="text-[var(--oh-text-secondary)]">Aucun installateur disponible</p>
              <p className="text-sm text-[var(--oh-text-muted)] mt-2">
                Créez d'abord un installateur dans le Hub Operations
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {installateurs.map((installateur) => {
                const isSelected = installateurActuel === installateur.id;

                return (
                  <div
                    key={installateur.id}
                    onClick={() => !saving && onSelectionner(installateur.id)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? "border-[var(--oh-success-border)] bg-[var(--oh-success-bg)]"
                        : "border-[var(--oh-border)] hover:border-[var(--oh-accent)] hover:bg-[var(--oh-accent-bg)]"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          isSelected ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" : "bg-[var(--oh-bg-elevated)]"
                        }`}>
                          {isSelected ? (
                            <Check className="w-6 h-6" />
                          ) : (
                            <Wrench className="w-6 h-6 text-[var(--oh-text-secondary)]" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-[var(--oh-text)]">
                              {installateur.raison_sociale || installateur.nom}
                            </h4>
                            <Badge 
                              variant="outline" 
                              className={installateur.type === "interne" ? "text-[var(--oh-accent-text)] border-[var(--oh-accent)]" : "text-[var(--oh-info-text)] border-[var(--oh-info-border)]"}
                            >
                              {installateur.type === "interne" ? "Interne" : "Externe"}
                            </Badge>
                            {installateur._source_tenant && (
                              <Badge variant="outline" className="text-[var(--oh-accent-text)] border-[var(--oh-accent)] text-xs">
                                Entreprise
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-[var(--oh-text-secondary)]">
                            {installateur.entreprise || installateur.nom}
                            {installateur.siret && ` • SIRET ${installateur.siret}`}
                          </p>
                          {/* Assurances status */}
                          <div className="flex gap-2 mt-1">
                            {(installateur.assurance_decennale || installateur.assurance_decennale_info) ? (
                              <span className="text-xs text-[var(--oh-success-text)] flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Décennale
                              </span>
                            ) : (
                              <span className="text-xs text-[var(--oh-info-text)] flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" /> Décennale ?
                              </span>
                            )}
                            {(installateur.assurance_rc_pro || installateur.assurance_rc_pro_info) && (
                              <span className="text-xs text-[var(--oh-success-text)] flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> RC Pro
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {/* RGE Badges */}
                        {installateur.certifications_rge?.length > 0 ? (
                          <div className="flex flex-wrap justify-end gap-1 mb-2">
                            {installateur.certifications_rge.slice(0, 3).map((item, idx) => {
                              const code = typeof item === 'object' ? item.code : item;
                              return (
                                <Badge key={idx} variant="outline" className="text-[var(--oh-success-text)] border-[var(--oh-success-border)] text-xs">
                                  {code}
                                </Badge>
                              );
                            })}
                            {installateur.certifications_rge.length > 3 && (
                              <Badge variant="outline" className="text-[var(--oh-text-secondary)] text-xs">
                                +{installateur.certifications_rge.length - 3}
                              </Badge>
                            )}
                          </div>
                        ) : installateur.specialites?.length > 0 ? (
                          <div className="flex flex-wrap justify-end gap-1 mb-2">
                            {installateur.specialites.slice(0, 3).map((spec, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {spec}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-[var(--oh-info-text)] border-[var(--oh-info-border)] text-xs mb-2">
                            RGE ?
                          </Badge>
                        )}
                        <p className="text-xs text-[var(--oh-text-secondary)]">
                          {installateur.telephone || installateur.email}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// ÉTAPE 4 : AIDES
// =============================================================================

function EtapeAides({ devis, client, caps = { showMPR: true }, aidesConfig, setAidesConfig, onCalculer, onUpdateClient, onPrimeCeeManuelle, saving, mentionsAides = [], aidesDelegataires = [], dossier = null, tenantId, onApplied, onDossierUpdated, dossierId }) {
  const aides = devis?.snapshot_aides;
  // Délégataire DYNAMIQUE (jamais en dur) : ligne prime du devis → snapshot_aides →
  // dossier.cee. Plus de fallback "Premium (par défaut)" codé en dur.
  const _primeLigneDeleg = (devis?.lignes || []).find(l => l.aide_type === "cee" && l.manuelle);
  const delegataireNom = _primeLigneDeleg?.delegataire_nom || aides?.delegataire_nom || dossier?.cee?.delegataire_nom || "";

  // Prime CEE saisie À LA MAIN (OMV) — pré-remplie depuis la ligne existante.
  const _primeLigne = (devis?.lignes || []).find(l => l.type_ligne === "aide_deduite" && l.aide_type === "cee" && l.manuelle);
  const [primeMontant, setPrimeMontant] = useState(_primeLigne ? Math.abs(_primeLigne.montant_ttc) : "");
  const [primeDeleg, setPrimeDeleg] = useState(_primeLigne?.delegataire_nom || "");
  const [primeSiren, setPrimeSiren] = useState(_primeLigne?.delegataire_siren || "");
  // Sélection d'un délégataire du Module Aides → propage nom + SIREN (ligne prime + mention).
  const onSelectDelegataire = (nom) => {
    setPrimeDeleg(nom);
    const d = (aidesDelegataires || []).find(x => (x.nom || "") === nom);
    setPrimeSiren(d?.siren || "");
  };

  // ─── Calcul prime CEE B2B (fiche BAT-TH-125) — source unique dossier.cee/batiment/entreprise ───
  // cumac = coef_zone(CP) × surface × facteur_secteur(secteur OPÉRATION). Le DEAL B2B (choisi
  // APRÈS le cumac) fournit prix_unitaire + fiche + délégataire : montant_ht = cumac × taux ;
  // prix_ht_vmc = montant_ht / nb_caissons ; prime_ttc = montant_ht × 1,20 (reste à charge 0).
  const _cee = dossier?.cee || {};
  // Champs ÉDITABLES dans le wizard, SYNCHRONISÉS avec le dossier (SOURCE UNIQUE) : éditer
  // ici écrit dans dossier.cee/batiment/technique/entreprise (PATCH), PAS une valeur locale.
  const [secteurOp, setSecteurOp] = useState(dossier?.cee?.secteur || "");
  const [surfaceVent, setSurfaceVent] = useState(dossier?.batiment?.surface_ventilee_m2 ?? "");
  const [nbCaissons, setNbCaissons] = useState(dossier?.technique?.nb_caissons || dossier?.batiment?.nb_caissons || 1);
  const [cpOp, setCpOp] = useState(dossier?.entreprise?.cp || dossier?.entreprise?.code_postal || dossier?.client_cp || "");
  // CP utilisé pour le CALCUL (zone climatique → cumac) : CP des TRAVAUX prioritaire.
  // Champ structuré `cp_travaux` d'abord (fiable), sinon extraction regex de l'adresse
  // travaux (rétro-compat dossiers anciens), sinon CP de facturation (`cpOp`).
  const _cpTravaux = (dossier?.batiment?.cp_travaux || "").trim()
    || (String(dossier?.batiment?.adresse_travaux || "").match(/\b(\d{5})\b/) || [])[1] || "";
  const cpCalcul = _cpTravaux || cpOp;
  const [savingDossier, setSavingDossier] = useState(false);
  // Re-sync quand le dossier change (rechargement / autre devis).
  useEffect(() => {
    setSecteurOp(dossier?.cee?.secteur || "");
    setSurfaceVent(dossier?.batiment?.surface_ventilee_m2 ?? "");
    setNbCaissons(dossier?.technique?.nb_caissons || dossier?.batiment?.nb_caissons || 1);
    setCpOp(dossier?.entreprise?.cp || dossier?.entreprise?.code_postal || dossier?.client_cp || "");
  }, [dossier?.id, dossier?._id]);
  const _champsManquants = [
    !secteurOp && "secteur", !(surfaceVent && Number(surfaceVent) > 0) && "surface ventilée", !cpOp && "code postal",
  ].filter(Boolean);
  // Écrit la correction DANS LE DOSSIER (merge non destructif des sous-objets) → source unique.
  const saveDossierCee = async () => {
    const dosId = dossierId || dossier?.id || dossier?._id;
    if (!dosId) return;
    setSavingDossier(true);
    try {
      const body = {
        cee: { ...(dossier.cee || {}), secteur: secteurOp || null },
        batiment: { ...(dossier.batiment || {}), surface_ventilee_m2: surfaceVent === "" ? null : Number(surfaceVent) },
        technique: { ...(dossier.technique || {}), nb_caissons: nbCaissons === "" ? null : Number(nbCaissons) },
        entreprise: { ...(dossier.entreprise || {}), cp: cpOp || null },
      };
      const res = await fetch(`${API}/api/metier/dossiers/${dosId}`, {
        method: "PATCH", headers: getAuthHeaders(tenantId, "application/json"), credentials: "include",
        body: JSON.stringify(body),
      });
      if (res.ok && onDossierUpdated) onDossierUpdated({ ...dossier, ...body });
    } finally { setSavingDossier(false); }
  };
  const [dealsB2B, setDealsB2B] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState("");
  // Zone climatique calculée depuis le CP utilisé (travaux prioritaire) — affichée au wizard.
  const [zoneWizard, setZoneWizard] = useState("");
  useEffect(() => {
    const cp = (cpCalcul || "").trim();
    if (!/^\d{4,5}$/.test(cp)) { setZoneWizard(""); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/api/metier/aides/zones/${cp}`, { headers: getAuthHeaders(tenantId), credentials: "include" });
        if (r.ok) { const d = await r.json(); setZoneWizard(d.zone || d.zone_climatique || ""); }
      } catch { /* noop */ }
    })();
  }, [cpCalcul, tenantId]);
  const [calcResult, setCalcResult] = useState(null);  // {cumac_kwh, montant_ht, prix_ht_vmc, prime_ttc, details}
  const [calculating, setCalculating] = useState(false);
  const [applying, setApplying] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/metier/aides/deals`, { headers: getAuthHeaders(tenantId), credentials: "include" });
        if (res.ok) {
          const all = await res.json();
          setDealsB2B((all || []).filter(d => (d.type || d.regime) === "b2b"));
        }
      } catch { /* noop */ }
    })();
  }, [tenantId]);
  const selectedDeal = dealsB2B.find(d => (d.id || "") === selectedDealId) || null;
  const calculerCumac = async () => {
    if (!selectedDeal) return;
    setCalculating(true);
    try {
      const res = await fetch(`${API}/api/metier/aides/fiche/bat-th-125/calcul-cumac`, {
        method: "POST", headers: getAuthHeaders(tenantId, "application/json"), credentials: "include",
        body: JSON.stringify({ code_postal: cpCalcul, secteur: secteurOp, surface_m2: Number(surfaceVent) || 0,
          prix_unitaire: Number(selectedDeal.prix_unitaire) || 0, nb_caissons: Number(nbCaissons) || 1,
          zone: dossier?.cee?.zone_climatique || null }),
      });
      if (res.ok) {
        const d = await res.json();
        setCalcResult(d);
        if (d.prime_ttc != null) setPrimeMontant(d.prime_ttc);  // proposition éditable
        if (selectedDeal.delegataire_nom || selectedDeal.delegataire_id) {
          const dn = selectedDeal.delegataire_nom || "";
          if (dn) onSelectDelegataire(dn);
        }
      }
    } finally { setCalculating(false); }
  };
  const appliquerCee = async () => {
    if (!calcResult) return;
    setApplying(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${devis.id}/cee-b2b`, {
        method: "POST", headers: getAuthHeaders(tenantId, "application/json"), credentials: "include",
        body: JSON.stringify({
          // 🔴 PLUS DE FICHE EN DUR. `|| "BAT-TH-125"` renvoyait un dossier
          // BAT-TH-126 en 125 dès que le deal ne portait pas sa fiche — une
          // valeur métier INVENTÉE, exactement ce qu'on ferme partout ailleurs.
          designation: selectedDeal?.operation || _cee.operation_cee
            || (selectedDeal?.fiche_cee || _cee.code_fiche
                ? `VMC (${selectedDeal?.fiche_cee || _cee.code_fiche})` : "VMC"),
          fiche_cee: selectedDeal?.fiche_cee || _cee.code_fiche || null,
          secteur: secteurOp, nb_caissons: Number(nbCaissons) || 1,
          prix_ht_vmc: calcResult.prix_ht_vmc, montant_ht: calcResult.montant_ht,
          // prime = montant éditable si Solène l'a changé, sinon prime_ttc calculée
          prime_ttc: Number(primeMontant) || calcResult.prime_ttc,
          volume_kwh_cumac: calcResult.cumac_kwh,
          delegataire_nom: selectedDeal?.delegataire_nom || primeDeleg || null,
          delegataire_siren: primeSiren || null,
          phrase: selectedDeal?.phrase || null,
          // deal source → phrase relue LIVE au rendu (dossier.cee.deal_id) ;
          // l'édition de la phrase du deal se reflètera sur le devis.
          deal_id: selectedDeal?.id || selectedDealId || null,
        }),
      });
      if (res.ok) { const d = await res.json(); if (d.devis && onApplied) onApplied(d.devis); }
    } finally { setApplying(false); }
  };

  // ─── MULTI-OPÉRATION (CC-DEV1-25) : le devis agrège dossier.operations[] ───
  // Source = operations[] du dossier (éditées dans la fiche, étape 2). Le deal fournit le
  // taux €/cumac appliqué à chaque op ; chaque op → 1 produit + 1 prime (reste à charge 0) ;
  // total = somme des primes. Aperçu via /operations/compute, application via /cee-b2b-operations.
  const dossierOps = dossier?.operations || [];
  const hasOperations = dossierOps.length > 0;
  const [opsPreview, setOpsPreview] = useState(null);
  useEffect(() => {
    if (!hasOperations) { setOpsPreview(null); return; }
    const prix = Number(selectedDeal?.prix_unitaire) || 0;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/metier/omv/operations/compute`, {
          method: "POST", headers: getAuthHeaders(tenantId, "application/json"), credentials: "include",
          body: JSON.stringify({ code_postal: cpCalcul, operations: dossierOps.map(o => ({ ...o, prix_unitaire: o.prix_unitaire || prix })) }),
        });
        if (res.ok) setOpsPreview(await res.json()); else setOpsPreview(null);
      } catch { /* noop */ }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(dossierOps), selectedDealId, cpCalcul, tenantId]);
  const appliquerCeeOperations = async () => {
    setApplying(true);
    try {
      const res = await fetch(`${API}/api/metier/devis/${devis.id}/cee-b2b-operations`, {
        method: "POST", headers: getAuthHeaders(tenantId, "application/json"), credentials: "include",
        body: JSON.stringify({
          prix_unitaire: Number(selectedDeal?.prix_unitaire) || 0,
          delegataire_nom: selectedDeal?.delegataire_nom || primeDeleg || null,
          delegataire_siren: primeSiren || null,
          phrase: selectedDeal?.phrase || null,
          deal_id: selectedDeal?.id || selectedDealId || null,
        }),
      });
      if (res.ok) { const d = await res.json(); if (d.devis && onApplied) onApplied(d.devis); toast.success("Opérations appliquées au devis"); }
      else { const e = await res.json().catch(() => ({})); toast.error(e?.detail || "Erreur application des opérations"); }
    } finally { setApplying(false); }
  };

  // Séparer les mentions par type
  const mentionsMpr = mentionsAides.filter(m => m.type_aide === "mpr");
  const mentionsCee = mentionsAides.filter(m => m.type_aide === "cee");

  // État du formulaire pour données fiscales manquantes
  const [showFiscalForm, setShowFiscalForm] = useState(false);
  const [fiscalData, setFiscalData] = useState({
    rfr: client?.rfr || "",
    nb_personnes_foyer: client?.nb_personnes_foyer || "",
    region_fiscale: client?.region_fiscale || "province"
  });
  const [savingFiscal, setSavingFiscal] = useState(false);

  // Vérifier si les données fiscales sont complètes
  const hasFiscalData = client?.rfr && client?.nb_personnes_foyer;

  // Gestion du clic sur "Calculer les aides"
  const handleCalculerClick = () => {
    if (!hasFiscalData) {
      setShowFiscalForm(true);
    } else {
      onCalculer();
    }
  };

  // Sauvegarder les données fiscales puis calculer
  const handleSaveFiscalData = async () => {
    if (!fiscalData.rfr || !fiscalData.nb_personnes_foyer) {
      toast.error("Veuillez renseigner le RFR et le nombre de personnes");
      return;
    }

    setSavingFiscal(true);
    try {
      const success = await onUpdateClient({
        rfr: parseFloat(fiscalData.rfr),
        nb_personnes_foyer: parseInt(fiscalData.nb_personnes_foyer),
        region_fiscale: fiscalData.region_fiscale
      });

      if (success) {
        setShowFiscalForm(false);
        toast.success("Données fiscales enregistrées");
        // Calculer les aides après mise à jour
        setTimeout(() => onCalculer(), 500);
      }
    } catch (error) {
      console.error("Erreur sauvegarde données fiscales:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingFiscal(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Prime CEE — chemin PRINCIPAL = calcul via le deal (fiche BAT-TH-125). */}
      {onPrimeCeeManuelle && (
        <Card className="border-[var(--oh-success-border)] bg-[var(--oh-success-bg)]">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Euro className="w-4 h-4 text-[var(--oh-success-text)]" /> Prime CEE (calcul fiche / deal)
            </CardTitle>
            <CardDescription>
              Sélectionnez le deal (taux €/cumac), calculez la prime depuis la fiche, puis
              appliquez : ajoute la ligne produit VMC + la prime au devis. Le montant reste
              ajustable manuellement ci-dessous si besoin.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* MULTI-OPÉRATION (CC-DEV1-25) — le devis agrège dossier.operations[].
                Le deal fournit le taux €/cumac ; 1 produit + 1 prime par opération ;
                total = somme des primes ; reste à charge 0. Source = fiche dossier (étape 2). */}
            {/* 🔴 LE DEAL — ET DONC LE DÉLÉGATAIRE — SE CHOISIT AU-DESSUS DES
                DEUX MODES.
                ⚠️ Ce sélecteur ne vivait QUE dans le bloc multi-opérations, alors
                que les DEUX modes consomment le même `selectedDeal` (mono via
                /cee-b2b, multi via /cee-b2b-operations). Le mode « prime couvre
                tout / RAC 0 » ne pouvait donc STRUCTURELLEMENT pas porter de
                délégataire : il en consommait un qu'aucun écran ne permettait de
                choisir — et c'est ce qui laissait l'ACE précédent en place.
                🔑 Le délégataire vient du DEAL : un seul choix, en haut, valable
                pour les deux modes. ⛔ Aucun défaut, aucun ACE automatique. */}
            <div className="rounded-md border border-[var(--oh-border)] p-3 space-y-2"
                 style={{ background: "var(--oh-bg-surface)" }} data-testid="cee-deal-commun">
              <Label htmlFor="cee-deal">Deal CEE (taux €/cumac) — définit le délégataire</Label>
              <select id="cee-deal" value={selectedDealId} onChange={e => setSelectedDealId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] text-[var(--oh-text)] px-3 py-2 text-sm"
                data-testid="cee-deal-select">
                <option value="">— Sélectionnez le délégataire CEE —</option>
                {dealsB2B.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.nom} — {d.prix_unitaire} €/cumac
                    {d.delegataire_nom ? ` · ${d.delegataire_nom}` : " · ⛔ sans délégataire"}
                    {d.fiche_cee ? ` (${d.fiche_cee})` : ""}
                  </option>
                ))}
              </select>
              {/* ⛔ UN DEAL SANS DÉLÉGATAIRE EST REFUSÉ, ⛔ pas complété en
                  silence. Mesuré en production : le deal « EFFY CERT » portait
                  delegataire_nom = null, donc le choisir ne sélectionnait
                  PERSONNE et la valeur précédente restait. */}
              {selectedDeal && !selectedDeal.delegataire_nom && (
                <p className="text-sm" style={{ color: "var(--oh-error-text)" }}
                   data-testid="cee-deal-sans-delegataire">
                  ⛔ Ce deal ne porte aucun délégataire. Sélectionnez le délégataire CEE
                  sur le deal avant d'appliquer — aucune valeur n'est déduite.
                </p>
              )}
              {selectedDeal?.delegataire_nom && (
                <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }}
                   data-testid="cee-deal-delegataire">
                  Délégataire : <b>{selectedDeal.delegataire_nom}</b>
                </p>
              )}
            </div>

            {hasOperations && (
              <div className="rounded-md border border-[var(--oh-success-border)] p-3 space-y-3" style={{ background: "var(--oh-success-bg)" }}>
                <div className="flex items-end">
                  <Button type="button" variant="outline" size="sm" disabled={applying || !selectedDeal}
                    className="border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
                    onClick={appliquerCeeOperations} data-testid="cee-ops-appliquer">
                    {applying ? "Application…" : `Appliquer ${dossierOps.length} opération(s) au devis`}
                  </Button>
                </div>
                <div className="rounded border border-[var(--oh-success-border)] divide-y divide-emerald-700/10" data-testid="cee-ops-preview">
                  {(opsPreview?.operations || dossierOps).map((op, i) => (
                    <div key={i} className="flex items-center justify-between px-2 py-1.5 text-sm">
                      <span className="font-medium">{op.fiche_ref || op.code_fiche || "—"}</span>
                      <span className="text-xs text-[var(--oh-text-muted)]">
                        {(op.secteur || "—")} · {op.surface || 0} m² · zone {op.zone || "—"}
                      </span>
                      <span>
                        <span className="font-medium">{Number(op.cumac || 0).toLocaleString('fr-FR')}</span> cumac
                        {op.prime ? <> · <span className="font-medium">{Number(op.prime).toLocaleString('fr-FR', {style:'currency',currency:'EUR'})}</span></> : null}
                      </span>
                    </div>
                  ))}
                  {opsPreview?.totals && (
                    <div className="flex items-center justify-between px-2 py-1.5 text-sm font-medium" data-testid="cee-ops-preview-total">
                      <span>Total {opsPreview.totals.nb_operations} opération(s)</span>
                      <span>
                        {Number(opsPreview.totals.total_cumac || 0).toLocaleString('fr-FR')} cumac
                        {opsPreview.totals.total_prime ? <> · {Number(opsPreview.totals.total_prime).toLocaleString('fr-FR', {style:'currency',currency:'EUR'})}</> : null}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-[var(--oh-text-muted)]">
                  Les opérations se modifient dans la fiche dossier (onglet Opérations CEE). Le devis additionne leurs primes → reste à charge 0.
                </p>
              </div>
            )}
            {/* Calcul cumac BAT-TH-125 mono-op (FALLBACK : dossiers SANS operations[]).
                coef_zone(CP) × facteur_secteur(secteur opération) × surface. */}
            {!hasOperations && (
            <div className="rounded-md border border-[var(--oh-success-border)] p-3 space-y-2" style={{ background: "var(--oh-success-bg)" }}>
              {/* Champs ÉDITABLES — synchronisés avec la fiche dossier (source unique).
                  onBlur → PATCH dossier ; le recalcul utilise la valeur corrigée. */}
              <div className="text-xs text-[var(--oh-text-muted)] mb-1">
                Fiche BAT-TH-125 — paramètres (modifiables, écrivent dans le dossier) ·
                {" "}Zone : <span className="font-medium" data-testid="cee-zone-wizard">{zoneWizard || "—"}</span>
                {_cpTravaux ? " (adresse travaux)" : (cpOp ? " (adresse principale)" : "")}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <Label className="text-xs">Secteur</Label>
                  <select value={secteurOp} onChange={e => setSecteurOp(e.target.value)} onBlur={saveDossierCee}
                    disabled={savingDossier} className="flex h-9 w-full rounded-md border border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] text-[var(--oh-text)] px-2 text-sm"
                    data-testid="cee-edit-secteur">
                    <option value="">—</option>
                    <option value="bureaux">Bureaux</option>
                    <option value="enseignement">Enseignement</option>
                    <option value="restauration">Restauration</option>
                    <option value="autres">Autres locaux</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Surface ventilée (m²)</Label>
                  <Input type="number" min="0" value={surfaceVent} onChange={e => setSurfaceVent(e.target.value)} onBlur={saveDossierCee}
                    disabled={savingDossier} className="h-9" data-testid="cee-edit-surface" />
                </div>
                <div>
                  <Label className="text-xs">Nb caissons</Label>
                  <Input type="number" min="1" value={nbCaissons} onChange={e => setNbCaissons(e.target.value)} onBlur={saveDossierCee}
                    disabled={savingDossier} className="h-9" data-testid="cee-edit-caissons" />
                </div>
                <div>
                  <Label className="text-xs">Code postal</Label>
                  <Input value={cpOp} onChange={e => setCpOp(e.target.value)} onBlur={saveDossierCee}
                    disabled={savingDossier} className="h-9" data-testid="cee-edit-cp" />
                </div>
                <div>
                  <Label className="text-xs">Zone (auto)</Label>
                  <Input readOnly value={zoneWizard ? `${zoneWizard}${_cpTravaux ? " (travaux)" : (cpOp ? " (principale)" : "")}` : "—"}
                    className="h-9 bg-[var(--oh-bg-surface)]" data-testid="cee-zone-field" />
                </div>
                <div>
                  <Label className="text-xs">Volume kWh cumac</Label>
                  <Input readOnly value={calcResult?.cumac_kwh || dossier?.cee?.volume_kwh_cumac || "— (à calculer)"}
                    className="h-9 bg-[var(--oh-bg-surface)]" data-testid="cee-cumac-field" />
                </div>
              </div>
              {_champsManquants.length > 0 && (
                <div className="text-xs text-[var(--oh-info-text)]" data-testid="cee-champs-manquants">
                  ⚠ À renseigner : {_champsManquants.join(", ")} (enregistré dans la fiche dossier).
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <Label htmlFor="cee-deal">Deal B2B (taux)</Label>
                  <select id="cee-deal" value={selectedDealId} onChange={e => setSelectedDealId(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] text-[var(--oh-text)] px-3 py-2 text-sm"
                    data-testid="cee-deal-select">
                    <option value="">— Sélectionner un deal —</option>
                    {dealsB2B.map(d => (
                      <option key={d.id} value={d.id}>{d.nom} — {d.prix_unitaire} €/cumac{d.fiche_cee ? ` (${d.fiche_cee})` : ""}</option>
                    ))}
                  </select>
                </div>
                <Button type="button" variant="outline" size="sm" disabled={calculating || !selectedDeal || !secteurOp || !surfaceVent}
                  onClick={calculerCumac} data-testid="cumac-calculer">
                  {calculating ? "Calcul…" : "Calculer la prime"}
                </Button>
                <Button type="button" variant="outline" size="sm" disabled={applying || !calcResult}
                  className="border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
                  onClick={appliquerCee} data-testid="cee-appliquer">
                  {applying ? "Application…" : "Appliquer (produit + prime)"}
                </Button>
              </div>
              {calcResult && (
                <div className="text-sm text-[var(--oh-text-muted)]" data-testid="cee-result">
                  Volume : <span className="font-medium">{Number(calcResult.cumac_kwh).toLocaleString('fr-FR')} kWh cumac</span>
                  {calcResult.details && <span className="text-xs text-[var(--oh-text-muted)]"> (zone {calcResult.details.zone} ×{calcResult.details.coef_zone}, secteur ×{calcResult.details.facteur_secteur})</span>}
                  {" · "}Montant HT : <span className="font-medium">{Number(calcResult.montant_ht).toLocaleString('fr-FR', {style:'currency',currency:'EUR'})}</span>
                  {" · "}Prix/VMC HT : <span className="font-medium">{Number(calcResult.prix_ht_vmc).toLocaleString('fr-FR', {style:'currency',currency:'EUR'})}</span>
                  {" · "}Prime TTC : <span className="font-medium">{Number(calcResult.prime_ttc).toLocaleString('fr-FR', {style:'currency',currency:'EUR'})}</span>
                </div>
              )}
            </div>
            )}
            <div className="text-xs font-medium text-[var(--oh-text-muted)] border-t border-[var(--oh-border)] pt-3">
              Ajustement manuel (optionnel) — override du montant / délégataire
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="prime-cee-montant">Montant de la prime (€ TTC) — éditable</Label>
                <Input id="prime-cee-montant" type="number" min="0" step="0.01"
                  placeholder="0,00" value={primeMontant}
                  onChange={e => setPrimeMontant(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="prime-cee-deleg">Délégataire CEE</Label>
                {aidesDelegataires && aidesDelegataires.length > 0 ? (
                  <select id="prime-cee-deleg" value={primeDeleg}
                    onChange={e => onSelectDelegataire(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] text-[var(--oh-text)] px-3 py-2 text-sm"
                    data-testid="prime-cee-delegataire-select">
                    <option value="">— Sélectionner —</option>
                    {aidesDelegataires.map(d => (
                      <option key={d.id || d.nom} value={d.nom || ""}>
                        {d.nom}{d.siren ? ` (SIREN ${d.siren})` : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input id="prime-cee-deleg" placeholder="ex : ACE ENERGIE"
                    value={primeDeleg} onChange={e => setPrimeDeleg(e.target.value)} />
                )}
                {primeSiren && <p className="text-xs text-[var(--oh-text-muted)] mt-1">SIREN : {primeSiren}</p>}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[var(--oh-text-muted)]">
                Reste à charge actuel : <span className="font-medium">{(devis?.reste_a_charge ?? devis?.total_ttc ?? 0).toLocaleString('fr-FR', {style:'currency', currency:'EUR'})}</span>
              </span>
              <div className="flex gap-2 items-center">
                {/* Le bouton 'Appliquer la prime' (manuel) est RETIRÉ : il posait une prime
                    décorrélée du produit → reste à charge négatif (DRAFT-00006). Chemin unique
                    = 'Appliquer (produit + prime)' ci-dessus (le montant édité sert d'override). */}
                <span className="text-xs text-[var(--oh-text-muted)]">Prime appliquée via « Appliquer (produit + prime) ».</span>
                <Button type="button" variant="outline" size="sm" disabled={saving}
                  onClick={() => { setPrimeMontant(""); setPrimeDeleg(""); setPrimeSiren(""); onPrimeCeeManuelle(0, null, null, null); }}>
                  Retirer la prime
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Moteur aides résidentielles MaPrimeRénov' (RFR / fiscal / calcul) —
          B2C uniquement (masqué B2B). En B2B/OMV, la prime CEE se saisit dans la
          carte manuelle ci-dessus ; pas de RFR/MPR. */}
      {caps.showMPR && (<>
      {/* Alerte si données fiscales manquantes */}
      {!hasFiscalData && !showFiscalForm && (
        <Card className="border-[var(--oh-info-border)] bg-[var(--oh-info-bg)]">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[var(--oh-info-bg)] flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-[var(--oh-info-text)]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-[var(--oh-info-text)]">Données fiscales incomplètes</h3>
                <p className="text-sm text-[var(--oh-info-text)] mt-1">
                  Pour calculer les aides MaPrimeRénov' et CEE, nous avons besoin du Revenu Fiscal de Référence (RFR) 
                  et du nombre de personnes du foyer fiscal du client.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFiscalForm(true)}
                  className="mt-3 border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]"
                  data-testid="btn-saisir-données-fiscales"
                >
                  Saisir les données fiscales
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Formulaire de saisie des données fiscales */}
      {showFiscalForm && (
        <Card className="border-[var(--oh-accent)] bg-[var(--oh-accent-bg)]">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Euro className="w-5 h-5 text-[var(--oh-accent-text)]" />
              Données fiscales du client
            </CardTitle>
            <CardDescription>
              Ces informations sont nécessaires pour calculer le montant des aides
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[var(--oh-text)]">
                  Revenu Fiscal de Référence (RFR) *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={fiscalData.rfr}
                    onChange={(e) => setFiscalData({ ...fiscalData, rfr: e.target.value })}
                    placeholder="Ex: 25000"
                    className="w-full px-3 py-2 border border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] text-[var(--oh-text)] rounded-lg focus:ring-2 focus:ring-[var(--oh-accent)] focus:border-[var(--oh-accent)]"
                    data-testid="input-rfr"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--oh-text-muted)]">€</span>
                </div>
                <p className="text-xs text-[var(--oh-text-secondary)]">
                  Indiqué sur l'avis d'imposition (N-1 ou N-2)
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[var(--oh-text)]">
                  Nombre de personnes du foyer *
                </label>
                <select
                  value={fiscalData.nb_personnes_foyer}
                  onChange={(e) => setFiscalData({ ...fiscalData, nb_personnes_foyer: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] text-[var(--oh-text)] rounded-lg focus:ring-2 focus:ring-[var(--oh-accent)] focus:border-[var(--oh-accent)]"
                  data-testid="select-nb-personnes"
                >
                  <option value="">Sélectionner...</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                    <option key={n} value={n}>{n} personne{n > 1 ? "s" : ""}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2 col-span-2">
                <label className="text-sm font-medium text-[var(--oh-text)]">
                  Région fiscale
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="region"
                      value="province"
                      checked={fiscalData.region_fiscale === "province"}
                      onChange={(e) => setFiscalData({ ...fiscalData, region_fiscale: e.target.value })}
                      className="w-4 h-4 text-[var(--oh-accent-text)]"
                    />
                    <span className="text-sm">Province</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="region"
                      value="idf"
                      checked={fiscalData.region_fiscale === "idf"}
                      onChange={(e) => setFiscalData({ ...fiscalData, region_fiscale: e.target.value })}
                      className="w-4 h-4 text-[var(--oh-accent-text)]"
                    />
                    <span className="text-sm">Île-de-France</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowFiscalForm(false)}
                disabled={savingFiscal}
              >
                Annuler
              </Button>
              <Button
                variant="outline"
                onClick={handleSaveFiscalData}
                disabled={savingFiscal || !fiscalData.rfr || !fiscalData.nb_personnes_foyer}
                className="border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
                data-testid="btn-enregistrer-fiscal"
              >
                {savingFiscal ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Enregistrer et calculer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configuration */}
      <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
        <CardHeader className="bg-[var(--oh-bg-surface)]">
          <CardTitle className="text-lg text-[var(--oh-text)]">Activation des aides</CardTitle>
          <CardDescription className="text-[var(--oh-text-secondary)]">
            Sélectionnez les aides à appliquer. Elles sont cumulables.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 bg-[var(--oh-bg-surface)]">
          <div className="flex items-center justify-between p-4 bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] rounded-lg">
            <div className="flex items-center gap-3">
              <Checkbox
                id="mpr"
                checked={aidesConfig.mpr_active}
                onCheckedChange={(checked) =>
                  setAidesConfig({ ...aidesConfig, mpr_active: checked })
                }
              />
              <div>
                <label htmlFor="mpr" className="font-medium text-[var(--oh-text)] cursor-pointer">
                  MaPrimeRénov'
                </label>
                <p className="text-sm text-[var(--oh-text-secondary)]">Aide de l'État versée par l'ANAH</p>
              </div>
            </div>
            {aides?.mpr_montant > 0 && (
              <p className="text-xl font-medium text-[var(--oh-accent-text)]">
                {formatMontant(aides.mpr_montant)}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between p-4 bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)] rounded-lg">
            <div className="flex items-center gap-3">
              <Checkbox
                id="cee"
                checked={aidesConfig.cee_active}
                onCheckedChange={(checked) =>
                  setAidesConfig({ ...aidesConfig, cee_active: checked })
                }
              />
              <div>
                <label htmlFor="cee" className="font-medium text-[var(--oh-text)] cursor-pointer">
                  Prime CEE
                </label>
                <p className="text-sm text-[var(--oh-text-secondary)]">
                  Certificats d'Économies d'Énergie • {delegataireNom}
                </p>
              </div>
            </div>
            {aides?.cee_montant > 0 && (
              <p className="text-xl font-medium text-[var(--oh-success-text)]">
                {formatMontant(aides.cee_montant)}
              </p>
            )}
          </div>

          <Button
            variant="outline"
            onClick={handleCalculerClick}
            disabled={saving || (!aidesConfig.mpr_active && !aidesConfig.cee_active)}
            className="w-full border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
            data-testid="btn-calculer-aides"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Calculator className="w-4 h-4 mr-2" />
            )}
            Calculer les aides
          </Button>
        </CardContent>
      </Card>

      {/* Résumé financier */}
      {aides && (
        <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
          <CardHeader className="bg-[var(--oh-bg-surface)]">
            <CardTitle className="text-lg text-[var(--oh-text)]">Reste à charge client</CardTitle>
          </CardHeader>
          <CardContent className="bg-[var(--oh-bg-surface)]">
            <div className="space-y-3">
              <div className="flex justify-between text-[var(--oh-text-secondary)]">
                <span>Total TTC</span>
                <span className="font-medium">{formatMontant(devis?.total_ttc)}</span>
              </div>
              {aides.mpr_montant > 0 && (
                <div className="flex justify-between text-[var(--oh-accent-text)]">
                  <span>− MaPrimeRénov' ({aides.couleur_mpr})</span>
                  <span className="font-medium">− {formatMontant(aides.mpr_montant)}</span>
                </div>
              )}
              {aides.cee_montant > 0 && (
                <div className="flex justify-between text-[var(--oh-success-text)]">
                  <span>− Prime CEE</span>
                  <span className="font-medium">− {formatMontant(aides.cee_montant)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between text-lg font-medium">
                <span>Reste à charge</span>
                <span className={devis?.reste_a_charge <= 0 ? "text-[var(--oh-success-text)]" : "text-[var(--oh-text)]"}>
                  {formatMontant(devis?.reste_a_charge)}
                </span>
              </div>
              {devis?.reste_a_charge <= 0 && (
                <p className="text-sm text-[var(--oh-success-text)] text-center mt-2">
                  Le client ne paie rien, il reçoit même un gain !
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      </>)}

      {/* Mentions obligatoires (MPR masqué en B2B ; CEE conservé) */}
      {(mentionsMpr.length > 0 || mentionsCee.length > 0) && (
        <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
          <CardHeader className="bg-[var(--oh-bg-surface)]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--oh-info-bg)] flex items-center justify-center">
                <Info className="w-5 h-5 text-[var(--oh-info-text)]" />
              </div>
              <div>
                <CardTitle className="text-lg">Mentions obligatoires</CardTitle>
                <CardDescription>
                  Ces mentions seront incluses dans le devis et le PDF
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Mentions MPR (B2C uniquement) */}
            {caps.showMPR && mentionsMpr.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-[var(--oh-accent-text)] flex items-center gap-2">
                  <Badge className="bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]">MPR</Badge>
                  MaPrimeRénov'
                </h4>
                {mentionsMpr.map((mention, index) => (
                  <div key={`mpr-${index}`} className="p-3 bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] rounded-lg">
                    <p className="font-medium text-sm text-[var(--oh-accent-text)] mb-1">{mention.titre}</p>
                    <p className="text-sm text-[var(--oh-text)]">{mention.texte}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Mentions CEE */}
            {mentionsCee.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-[var(--oh-success-text)] flex items-center gap-2">
                  <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border border-[var(--oh-success-border)]">CEE</Badge>
                  Prime Énergie
                </h4>
                {mentionsCee.map((mention, index) => (
                  <div key={`cee-${index}`} className="p-3 bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)] rounded-lg">
                    <p className="font-medium text-sm text-[var(--oh-success-text)] mb-1">{mention.titre}</p>
                    <p className="text-sm text-[var(--oh-text)]">{mention.texte}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// ÉTAPE 5 : VALIDATION + CGV + LOGOS
// =============================================================================

function EtapeValidation({ devis, dossier, validation, onEnregistrer, saving, installateurs, tenantId, etapesIds = [] }) {
  const aides = devis?.snapshot_aides;
  // Blocs d'une étape absente de la composition du tenant = sans objet (pas « Non défini »).
  const avecInstallateur = etapesIds.includes(ETAPE_INSTALLATEUR);
  const avecAides = etapesIds.includes(ETAPE_AIDES);
  const [identite, setIdentite] = useState(null);
  const [loadingIdentite, setLoadingIdentite] = useState(true);
  // Mention CEE/Ministère = SOURCE config (Mentions Obligatoires) — la MÊME que le PDF.
  const [mentionCee, setMentionCee] = useState("");
  const API = process.env.REACT_APP_BACKEND_URL;
  // Phrase prime délégataire = celle rendue depuis le DEAL, stockée sur le dossier.
  const phrasePrime = dossier?.cee?.phrase_prime || "";
  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API}/api/platform/tenants/${tenantId}/mentions`, {
          headers: { "X-Tenant-ID": tenantId, ...(token && { Authorization: `Bearer ${token}` }) },
          credentials: 'include',
        });
        if (res.ok) { const d = await res.json(); setMentionCee(d?.mentions?.cee?.content || ""); }
      } catch { /* noop */ }
    })();
  }, [tenantId, API]);
  
  // Trouver l'installateur sélectionné
  const installateurSelectionne = installateurs?.find(i => i.id === devis?.installateur_id);

  // Charger l'identité du tenant (CGV + Logos)
  useEffect(() => {
    const chargerIdentite = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const headers = {
          "X-Tenant-ID": tenantId || "climatpac",
          ...(token && { "Authorization": `Bearer ${token}` })
        };
        const res = await fetch(`${API}/api/platform/tenants/${tenantId}/identite`, {
          headers,
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setIdentite(data.identite);
        }
      } catch (error) {
        console.error("Erreur chargement identité:", error);
      } finally {
        setLoadingIdentite(false);
      }
    };
    if (tenantId) chargerIdentite();
  }, [tenantId, API]);

  // Extraire les données
  const cgv = identite?.cgv;
  const logo = identite?.logo || { url: identite?.logo_url };
  const qualifications = identite?.rge_qualifications || [];

  return (
    <div className="space-y-6">
      {/* En-tête avec Logo entreprise + Qualifications RGE */}
      <Card className="overflow-hidden">
        <CardHeader className="bg-[var(--oh-bg-surface)] border-b">
          <div className="flex items-center justify-between">
            {/* Logo entreprise */}
            <div className="flex items-center gap-4">
              {logo?.url ? (
                <img 
                  src={logo.url} 
                  alt={identite?.raison_sociale || "Logo entreprise"} 
                  className="h-16 w-auto max-w-[180px] object-contain"
                />
              ) : (
                <div className="h-16 w-16 bg-[var(--oh-bg-surface)] rounded-lg flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-[var(--oh-text-muted)]" />
                </div>
              )}
              <div>
                <h2 className="text-xl font-medium text-[var(--oh-text)]">
                  {identite?.raison_sociale || "Entreprise"}
                </h2>
                {identite?.siret && (
                  <p className="text-sm text-[var(--oh-text-secondary)]">SIRET: {identite.siret}</p>
                )}
              </div>
            </div>
            
            {/* Logos Qualifications RGE */}
            {qualifications.length > 0 && (
              <div className="flex items-center gap-3">
                {qualifications.map((qualif, idx) => (
                  <div key={idx} className="flex flex-col items-center">
                    {qualif.logo_url ? (
                      <img 
                        src={qualif.logo_url} 
                        alt={qualif.code} 
                        className="h-12 w-auto object-contain"
                      />
                    ) : (
                      <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border border-[var(--oh-success-border)] text-xs">{qualif.code}</Badge>
                    )}
                    {qualif.numero && (
                      <span className="text-xs text-[var(--oh-text-secondary)] mt-1">N° {qualif.numero}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <p className="text-sm text-[var(--oh-text-secondary)]">
            Ces éléments (logo, qualifications) seront figés dans le devis au moment du verrouillage.
          </p>
        </CardContent>
      </Card>

      {/* Résumé devis */}
      <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
        <CardHeader className="bg-[var(--oh-bg-surface)]">
          <CardTitle className="text-lg text-[var(--oh-text)]">Récapitulatif du devis</CardTitle>
        </CardHeader>
        <CardContent className="bg-[var(--oh-bg-surface)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Financier */}
            <div className="space-y-4">
              <h3 className="font-medium text-[var(--oh-text)] border-b pb-2">Montants</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--oh-text-secondary)]">Total HT</span>
                  <span className="font-medium">{formatMontant(devis?.total_ht)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--oh-text-secondary)]">TVA</span>
                  <span className="font-medium">{formatMontant(devis?.total_tva)}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-[var(--oh-text)]">Total TTC</span>
                  <span className="font-medium">{formatMontant(devis?.total_ttc)}</span>
                </div>
                {avecAides && <Separator className="my-2" />}
                {avecAides && aides?.mpr_montant > 0 && (
                  <div className="flex justify-between text-[var(--oh-accent-text)]">
                    <span>MaPrimeRénov'</span>
                    <span>− {formatMontant(aides.mpr_montant)}</span>
                  </div>
                )}
                {avecAides && aides?.cee_montant > 0 && (
                  <div className="flex justify-between text-[var(--oh-success-text)]">
                    <span>Prime CEE</span>
                    <span>− {formatMontant(aides.cee_montant)}</span>
                  </div>
                )}
                {avecAides && (
                  <div className="flex justify-between text-lg font-medium pt-2 border-t">
                    <span>Reste à charge</span>
                    <span className={devis?.reste_a_charge <= 0 ? "text-[var(--oh-success-text)]" : ""}>
                      {formatMontant(devis?.reste_a_charge)}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Validité du devis */}
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium text-[var(--oh-text)] mb-2">Validité du devis</h4>
                <select
                  value={devis?.duree_validite_jours || 30}
                  onChange={(e) => {
                    // Cette fonction serait passée via props pour modifier le devis
                    console.log("Changer validité:", e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] text-[var(--oh-text)] rounded-lg text-sm focus:ring-2 focus:ring-[var(--oh-accent)] focus:border-[var(--oh-accent)]"
                >
                  <option value={15}>15 jours</option>
                  <option value={30}>30 jours (par défaut)</option>
                  <option value={45}>45 jours</option>
                  <option value={60}>60 jours</option>
                  <option value={90}>90 jours</option>
                </select>
                {devis?.date_creation && (
                  <p className="text-xs text-[var(--oh-text-secondary)] mt-1">
                    Valide jusqu'au {new Date(new Date(devis.date_creation).getTime() + (devis?.duree_validite_jours || 30) * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            </div>

            {/* Intervenants — seulement les blocs des étapes que le tenant propose */}
            {(avecInstallateur || avecAides) && (
            <div className="space-y-4" data-testid="devis-intervenants">
              <h3 className="font-medium text-[var(--oh-text)] border-b pb-2">Intervenants</h3>
              <div className="space-y-3 text-sm">
                {avecInstallateur && (
                <div>
                  <p className="text-[var(--oh-text-secondary)] text-xs mb-1">Installateur</p>
                  <p className="font-medium text-[var(--oh-text)]">
                    {installateurSelectionne?.entreprise || installateurSelectionne?.raison_sociale || installateurSelectionne?.nom || (devis?.installateur_id ? "✓ Sélectionné" : "⚠ Non défini")}
                  </p>
                  {installateurSelectionne?.siret && (
                    <p className="text-xs text-[var(--oh-text-secondary)]">SIRET: {installateurSelectionne.siret}</p>
                  )}
                </div>
                )}
                {avecAides && (
                <>
                <div>
                  <p className="text-[var(--oh-text-secondary)] text-xs mb-1">Délégataire CEE</p>
                  <p className="font-medium text-[var(--oh-text)]">
                    {aides?.delegataire_nom || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[var(--oh-text-secondary)] text-xs mb-1">Couleur MPR</p>
                  <p className="font-medium text-[var(--oh-text)] capitalize">
                    {aides?.couleur_mpr || "—"}
                  </p>
                </div>
                </>
                )}
              </div>
            </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* CGV */}
      <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
        <CardHeader className="bg-[var(--oh-bg-surface)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--oh-accent-bg)] flex items-center justify-center">
                <FileText className="w-5 h-5 text-[var(--oh-accent-text)]" />
              </div>
              <div>
                <CardTitle className="text-lg">Conditions Générales de Vente</CardTitle>
                <CardDescription>
                  {cgv?.version_courante ? `Version ${cgv.version_courante}` : "Version actuelle"}
                </CardDescription>
              </div>
            </div>
            {cgv?.version_courante && (
              <Badge className="bg-[var(--oh-accent-solid)]">v{cgv.version_courante}</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="bg-[var(--oh-bg-surface)]">
          {loadingIdentite ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--oh-accent-text)]" />
            </div>
          ) : cgv?.contenu_html ? (
            <div className="bg-[var(--oh-bg-elevated)] border rounded-lg p-4 max-h-64 overflow-y-auto">
              <div 
                className="cgv-preview max-w-none"
                dangerouslySetInnerHTML={{ __html: cgv.contenu_html }}
              />
            </div>
          ) : (
            <div className="text-center py-6 text-[var(--oh-text-secondary)]">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Les CGV seront jointes au devis lors de la validation.</p>
              <p className="text-xs mt-1">Configurez vos CGV dans Paramètres → Identité</p>
            </div>
          )}
          <p className="text-xs text-[var(--oh-text-secondary)] mt-3 flex items-center gap-1">
            <Info className="w-3 h-3" />
            Les CGV seront automatiquement snapshotées et jointes au PDF du devis
          </p>
        </CardContent>
      </Card>

      {/* MENTIONS (fin de devis) — LECTURE, reflètent ce qui sort sur le PDF. Sources :
          phrase prime (deal → dossier.cee.phrase_prime) + mention CEE/Ministère (Mentions
          Obligatoires, config). Pas de réordonnancement, juste l'aperçu comme les CGV. */}
      {(phrasePrime || mentionCee) && (
        <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]" data-testid="recap-mentions">
          <CardHeader className="bg-[var(--oh-bg-surface)]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--oh-success-bg)] flex items-center justify-center">
                <FileText className="w-5 h-5 text-[var(--oh-success-text)]" />
              </div>
              <div>
                <CardTitle className="text-lg">Mentions (fin de devis)</CardTitle>
                <CardDescription>Telles qu'elles sortiront sur le PDF</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="bg-[var(--oh-bg-surface)] space-y-3">
            {phrasePrime && (
              <div className="bg-[var(--oh-bg-elevated)] border rounded-lg p-3">
                <p className="text-xs font-medium text-[var(--oh-text-secondary)] mb-1">Prime CEE (sous le montant)</p>
                <p className="text-sm text-[var(--oh-text-secondary)]" style={{ whiteSpace: "pre-line" }}>{phrasePrime}</p>
              </div>
            )}
            {mentionCee && (
              <div className="bg-[var(--oh-bg-elevated)] border rounded-lg p-3">
                <p className="text-xs font-medium text-[var(--oh-text-secondary)] mb-1">Mention CEE réglementaire</p>
                <p className="text-sm text-[var(--oh-text-secondary)]" style={{ whiteSpace: "pre-line" }}>{mentionCee}</p>
              </div>
            )}
            <p className="text-xs text-[var(--oh-text-secondary)] flex items-center gap-1">
              <Info className="w-3 h-3" />
              Source : Mentions Obligatoires (Module Aides) + deal. Lecture seule.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Installateur - Qualification & Assurance */}
      {installateurSelectionne && (
        <Card className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
          <CardHeader className="bg-[var(--oh-bg-surface)]">
            <CardTitle className="text-lg text-[var(--oh-text)]">Installateur - Qualifications & Assurance</CardTitle>
          </CardHeader>
          <CardContent className="bg-[var(--oh-bg-surface)]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Qualifications RGE */}
              <div className="space-y-2">
                <h4 className="font-medium text-[var(--oh-text)]">Qualifications RGE</h4>
                {installateurSelectionne.certifications_rge?.length > 0 ? (
                  <div className="space-y-2">
                    {installateurSelectionne.certifications_rge.map((cert, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-[var(--oh-success-bg)] rounded">
                        <span className="font-medium text-[var(--oh-success-text)]">
                          {typeof cert === 'object' ? cert.code : cert}
                        </span>
                        {typeof cert === 'object' && cert.numero && (
                          <span className="text-xs text-[var(--oh-text-secondary)]">N° {cert.numero}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : installateurSelectionne.specialites?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {installateurSelectionne.specialites.map((spec, idx) => (
                      <Badge key={idx} variant="outline" className="text-[var(--oh-accent-text)] border-[var(--oh-accent)]">
                        {spec}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-[var(--oh-info-text)] text-sm">⚠ Non renseignées</p>
                )}
              </div>

              {/* Assurance décennale */}
              <div className="space-y-2">
                <h4 className="font-medium text-[var(--oh-text)]">Assurance décennale</h4>
                {installateurSelectionne.assurance_decennale ? (
                  <div className="p-3 bg-[var(--oh-accent-bg)] rounded">
                    <p className="font-medium text-[var(--oh-accent-text)]">
                      {installateurSelectionne.assurance_decennale}
                    </p>
                    {installateurSelectionne.assurance_numero && (
                      <p className="text-sm text-[var(--oh-text-secondary)]">
                        N° police : {installateurSelectionne.assurance_numero}
                      </p>
                    )}
                    {installateurSelectionne.assurance_validite && (
                      <p className="text-sm text-[var(--oh-text-secondary)]">
                        Validité : {installateurSelectionne.assurance_validite}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-[var(--oh-info-text)] text-sm">⚠ Non renseignée</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Validation */}
      <Card className={validation.valide ? "border-[var(--oh-success-border)]" : "border-[var(--oh-error-border)]"}>
        <CardHeader>
          <div className="flex items-center gap-3">
            {validation.valide ? (
              <CheckCircle2 className="w-6 h-6 text-[var(--oh-success-text)]" />
            ) : (
              <XCircle className="w-6 h-6 text-[var(--oh-error-text)]" />
            )}
            <div>
              <CardTitle className={validation.valide ? "text-[var(--oh-success-text)]" : "text-[var(--oh-error-text)]"}>
                {validation.valide ? "Prêt à verrouiller" : "Validation incomplète"}
              </CardTitle>
              <CardDescription>
                {validation.valide
                  ? "Toutes les conditions sont remplies"
                  : "Corrigez les erreurs ci-dessous"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        {!validation.valide && validation.erreurs?.length > 0 && (
          <CardContent>
            <ul className="space-y-2">
              {validation.erreurs.map((erreur, index) => (
                <li key={index} className="flex items-center gap-2 text-[var(--oh-error-text)]">
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{erreur}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        )}
      </Card>

      {/* Bandeau « prêt à enregistrer » — INFORMATIF. Le bouton d'action UNIQUE est dans
          le pied de page du wizard (CC-DEV1-27 : suppression du doublon d'enregistrement). */}
      {validation.valide && (
        <OhStatusNote variant="success" title="Devis prêt à enregistrer" data-testid="recap-pret-enregistrer">
          Le devis sera enregistré et restera modifiable jusqu'à l'envoi en signature.
          Il sera verrouillé automatiquement au retour de la signature.
          {" "}Cliquez sur <span className="font-medium">« Enregistrer le devis »</span> en bas de page.
        </OhStatusNote>
      )}
    </div>
  );
}
